import React, { useState, useEffect, Fragment, useMemo, useRef } from 'react';
import './Naomi.scss';
import { NT, NI, NMButton, NMChip } from './NaomiTheme';
import { MDialog, MTextField, MSwitch } from './NaomiUi';
import { useSDMState, LivePreview, BranchBadge, CalcBadge } from './NaomiShared';
import {
  DataTypePicker, ValidationEditor, FtInPreview, ValidationSummary,
  newBlankSDM, newAssetSDM, forkSDM, snapshotSDM, UnitsManager, ForkPickerDialog,
  validationsToRangeCheck, rangeCheckToValidations,
} from './NaomiFieldTypes';
import { TypeIcon, ConditionBuilder, CalcBuilder, NewQuestionDialog, PublishDialog, ConfirmDialog, MappingBuilderDialog } from './NaomiBuilders';
import { OutlineTree } from './NaomiOutline';
import { getNaomiRegistry, setNaomiCustomTypes } from './NaomiRegistry';
import { MappingPane } from './mapping/MappingPane';
import { MappingTradeSelector } from './mapping/MappingTradeSelector';
import { FEATURE_FLAGS } from './featureFlags';
import { MappingScopeSelect, PROVIDER_SCOPE } from './mapping/MappingScopeSelect';
import { MappingSaveTargets } from './mapping/MappingSaveTargets';
import { getTaskTemplateForScope } from './mapping/services/MappingService';
import { getTenantsByTrade, type TenantOption } from './mapping/services/TenantMappingService';
import { saveTaskTemplateToScope } from './mapping/services/TradeTemplateSaveService';
import { validateTaskTemplateJson, validateSdmStructure } from './mapping/services/templateValidation';
import { syncToSession, applyLinksToSdm, tasksToStorage } from './mapping/services/SdmCodeGen';
import type { Task, LinkDef } from './mapping/types';
import { useOidcAccessToken } from '@axa-fr/react-oidc';
import { saveNaomiTemplate, saveNaomiAssetTemplate, loadNaomiTemplate, loadNaomiTemplateByTradeOid } from './services/NaomiService';
import { deserializeFromSdmDocument } from './utils/sdm-deserializer';
import { fileToLogoDataUrl } from './utils/image-utils';
import { serializeToSdmDocument } from './utils/sdm-serializer';
import { getTrades, updateTrade, deleteTrade } from '../services/TradeConfigurationServics';
import { CataloguePane } from './CataloguePane';
import { getCatalogueImages } from './services/CatalogueService';

export function mergeVersions(base, delta) {
  const mergeArr = (b, d) => {
    const map = new Map((b || []).map(i => [i.OID, i]));
    (d || []).forEach(i => map.set(i.OID, i));
    return Array.from(map.values());
  };
  return {
    ...delta,
    ItemDef:      mergeArr(base.ItemDef,      delta.ItemDef),
    ItemGroupDef: mergeArr(base.ItemGroupDef, delta.ItemGroupDef),
    FormDef:      mergeArr(base.FormDef,      delta.FormDef),
    EventDef:     mergeArr(base.EventDef,     delta.EventDef),
    MethodDef:    mergeArr(base.MethodDef,    delta.MethodDef),
    ConditionDef: mergeArr(base.ConditionDef, delta.ConditionDef),
    CodeList:     mergeArr(base.CodeList,     delta.CodeList),
    Protocol: delta.Protocol?.EventRef?.length ? delta.Protocol : base.Protocol,
    Images:   delta.Images?.length            ? delta.Images   : base.Images,
  };
}

function resolveVersionChain(versions) {
  if (!versions || versions.length === 0) return null;
  if (versions.length === 1) return { ...versions[0] };
  return versions.reduce((acc, v) => mergeVersions(acc, v));
}

export function computeDelta(current, base) {
  const diffArr = (curr, prev) => (curr || []).filter(item => {
    const p = (prev || []).find(b => b.OID === item.OID);
    return !p || JSON.stringify(item) !== JSON.stringify(p);
  });
  return {
    ...current,
    ItemDef:      diffArr(current.ItemDef,      base.ItemDef),
    ItemGroupDef: diffArr(current.ItemGroupDef, base.ItemGroupDef),
    FormDef:      diffArr(current.FormDef,      base.FormDef),
    EventDef:     diffArr(current.EventDef,     base.EventDef),
    MethodDef:    diffArr(current.MethodDef,    base.MethodDef),
    ConditionDef: diffArr(current.ConditionDef, base.ConditionDef),
    CodeList:     diffArr(current.CodeList,     base.CodeList),
    Images:       current.Images?.length ? current.Images : base.Images,

  };
}

function findRefInfo(tv, itemOid) {
  function search(groups, parentOid) {
    for (const g of groups) {
      const r = g.ItemRef.find(rf => rf.ItemOID === itemOid);
      if (r) return { group: g, parentOid, ref: r };
      const found = search(g.Subgroups || [], g.OID);
      if (found) return found;
    }
    return null;
  }
  return search(tv.ItemGroupDef, null);
}

function findGroupAnywhere(tv, groupOid) {
  function search(groups, parentOid): any {
    for (const g of groups) {
      if (g.OID === groupOid) return { group: g, parentOid };
      const found = search(g.Subgroups || [], g.OID);
      if (found) return found;
    }
    return null;
  }
  return search(tv.ItemGroupDef, null);
}

function mapGroup(v, groupOid, mutate) {
  function walk(groups): any[] {
    return groups.map(g => {
      if (g.OID === groupOid) return mutate(g);
      if (!(g.Subgroups?.length)) return g;
      return { ...g, Subgroups: walk(g.Subgroups) };
    });
  }
  return walk(v.ItemGroupDef);
}

function extractRepeatTriggerOid(meth: any): string | null {
  if (!meth || meth.kind !== 'repeat') return null;
  const m = (meth.FormalExpression?.text || '').match(/!\(([^\s:)]+)/);
  return m ? m[1].trim() : null;
}

interface FormCanvasProps {
  sdm: any;
  selectedOid: string | null;
  onSelect: (oid: string | null) => void;
  density: string;
  onAddQuestion: (groupOid: string) => void;
  onDeleteQuestion: (groupOid: string, itemOid: string) => void;
  onTitleEdit: (name: string) => void;
  savedAt: string | null;
  lastChanged: string;
  onAddBranchToGroup: (groupOid: string) => void;
  onClearGroupBranch: (groupOid: string) => void;
  onConfigureRepeating: (groupOid: string) => void;
  readonly: boolean;
}

function FormCanvas({ sdm, selectedOid, onSelect, density, onAddQuestion, onDeleteQuestion, onTitleEdit, savedAt, lastChanged, onAddBranchToGroup, onClearGroupBranch, onConfigureRepeating, readonly }: FormCanvasProps) {
  const tv = sdm.TemplateVersion[0];
  const itemByOid  = Object.fromEntries(tv.ItemDef.map(i => [i.OID, i]));
  const muByOid    = Object.fromEntries(sdm.BasicDefinition.MeasurementUnit.map(m => [m.OID, m]));
  const condByOid  = Object.fromEntries(tv.ConditionDef.map(c => [c.OID, c]));
  const methByOid  = Object.fromEntries(tv.MethodDef.map(m => [m.OID, m]));
  const dens = density === 'dense' ? { qPad: 8, gap: 4, group: 14 } : { qPad: 12, gap: 8, group: 22 };

  const renderQuestion = (item, ref, groupOid) => {
    if (!item) return null;
    const sel = selectedOid === item.OID;
    const mu = item.MeasurementOid ? muByOid[item.MeasurementOid] : null;
    const cond = ref.CollectionExceptionConditionOID ? condByOid[ref.CollectionExceptionConditionOID] : null;
    const meth = ref.MethodOID ? methByOid[ref.MethodOID] : null;
    return (
      <div key={item.OID + '-' + ref.ItemOID} onClick={() => onSelect(item.OID)} className="naomi-q"
        style={{ position: 'relative', padding: dens.qPad + 'px 14px',
          marginBottom: dens.gap, borderRadius: 8, cursor: 'pointer',
          background: sel ? NT.primarySoft + '80' : NT.surface,
          border: `1px solid ${sel ? NT.primary : NT.line}`,
          boxShadow: sel ? `0 0 0 3px ${NT.primarySoft}` : 'none',
          transition: 'all .12s' }}>
        {sel && <div style={{ position: 'absolute', top: -10, left: 10, fontSize: 10, fontWeight: 700,
          color: '#fff', background: NT.primary, padding: '1px 7px', borderRadius: 3,
          textTransform: 'uppercase', letterSpacing: .5 }}>Editing</div>}
        <button id="naomi-studio-button-question-delete" className="naomi-q-del" onClick={e => { e.stopPropagation(); onDeleteQuestion(groupOid, item.OID); }}
          style={{ position: 'absolute', top: 6, right: 6, width: 22, height: 22, border: 'none',
            background: 'transparent', color: NT.ink4, borderRadius: 4, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', opacity: 0,
            transition: 'opacity .12s, background .12s, color .12s' }}
          onMouseEnter={e => { e.currentTarget.style.background = NT.dangerSoft; e.currentTarget.style.color = NT.danger; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = NT.ink4; }}
          title="Delete question">
          <NI.Trash/>
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <TypeIcon type={item.DataType}/>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: NT.ink, flex: 1, paddingRight: 22 }}>
            {item.Question.TranslatedText.text}
            {ref.Mandatory === 'Yes' && <span style={{ color: NT.danger, marginLeft: 4 }}>*</span>}
          </span>
          <span style={{ fontSize: 10.5, color: NT.ink4, fontFamily: NT.fontMono }}>{item.OID}</span>
        </div>
        {item.Description && <div style={{ fontSize: 12, color: NT.ink3, marginBottom: 6 }}>{item.Description}</div>}

        <div style={{ pointerEvents: 'none' }}>
          {item.DataType === 'text' && <div style={{ height: 32, borderRadius: 4, border: `1px solid ${NT.line2}`, background: '#fff' }}/>}
          {item.DataType === 'multilinetext' && <div style={{ height: 80, borderRadius: 4, border: `1px solid ${NT.line2}`, background: '#fff', resize: 'none' as any }}/>}
          {(item.DataType === 'integer' || item.DataType === 'float') && <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ flex: 1, height: 32, borderRadius: 4, border: `1px solid ${NT.line2}`, background: '#fff' }}/>
            {mu && <div style={{ height: 32, padding: '0 12px', borderRadius: 4, background: NT.surfaceAlt,
              display: 'inline-flex', alignItems: 'center', fontSize: 12, fontWeight: 600, color: NT.ink3 }}>{mu.Symbol}</div>}
          </div>}
          {item.DataType === 'currency' && <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ height: 32, padding: '0 12px', borderRadius: 4, background: NT.surfaceAlt,
              display: 'inline-flex', alignItems: 'center', fontSize: 13, fontWeight: 600, color: NT.ink2 }}>$</div>
            <div style={{ flex: 1, height: 32, borderRadius: 4, border: `1px solid ${NT.line2}`, background: '#fff' }}/>
          </div>}
          {item.DataType === 'percent' && <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ flex: 1, height: 32, borderRadius: 4, border: `1px solid ${NT.line2}`, background: '#fff' }}/>
            <div style={{ height: 32, padding: '0 12px', borderRadius: 4, background: NT.surfaceAlt,
              display: 'inline-flex', alignItems: 'center', fontSize: 13, fontWeight: 600, color: NT.ink3 }}>%</div>
          </div>}
          {item.DataType === 'feetinches' && <FtInPreview/>}
          {item.DataType === 'image' && (getNaomiRegistry().canvasPreviewExtras.image
            ? getNaomiRegistry().canvasPreviewExtras.image(item)
            : <div style={{ height: 32 }}/>)}
          {item.DataType === 'composite' && (getNaomiRegistry().canvasPreviewExtras.composite
            ? getNaomiRegistry().canvasPreviewExtras.composite(item)
            : <div style={{ height: 32 }}/>)}
          {item.DataType === 'date' && <div style={{ height: 32, padding: '0 12px', borderRadius: 4, border: `1px solid ${NT.line2}`,
            background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: NT.ink4 }}>
            <span>mm/dd/yyyy</span><span style={{ color: NT.ink3 }}><NI.Cal/></span></div>}
          {item.DataType === 'time' && <div style={{ height: 32, padding: '0 12px', borderRadius: 4, border: `1px solid ${NT.line2}`,
            background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: NT.ink4 }}>
            <span>hh : mm</span><span style={{ color: NT.ink3 }}><NI.Clock/></span></div>}
          {item.DataType === 'datetime' && <div style={{ height: 32, padding: '0 12px', borderRadius: 4, border: `1px solid ${NT.line2}`,
            background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: NT.ink4 }}>
            <span>mm/dd/yyyy · hh:mm</span><span style={{ color: NT.ink3 }}><NI.Cal/></span></div>}
          {item.DataType === 'boolean' && <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ height: 30, padding: '0 14px', borderRadius: 4, background: NT.surfaceAlt,
              display: 'inline-flex', alignItems: 'center', fontSize: 12, color: NT.ink3 }}>Yes</div>
            <div style={{ height: 30, padding: '0 14px', borderRadius: 4, background: NT.surfaceAlt,
              display: 'inline-flex', alignItems: 'center', fontSize: 12, color: NT.ink3 }}>No</div>
          </div>}
          {(item.DataType === 'singleselect' || item.DataType === 'multiselect') && (() => {
            const cl = tv.CodeList.find(c => c.OID === item.CodeListRef?.CodeListOID);
            return <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {cl?.CodeListItem.slice(0, 5).map(ci => (
                <div key={ci.CodedValue} style={{ padding: '4px 10px', borderRadius: 4, fontSize: 11.5,
                  background: NT.surfaceAlt, color: NT.ink3 }}>{ci.Decode.TranslatedText.text}</div>
              ))}
              {cl && cl.CodeListItem.length > 5 && <div style={{ fontSize: 11.5, color: NT.ink4, padding: '4px 6px' }}>+{cl.CodeListItem.length - 5}</div>}
            </div>;
          })()}
        </div>

        {(cond || meth || item.RangeCheck?.length || item.Validations) && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {cond && <BranchBadge cond={cond}/>}
          {meth && <CalcBadge method={meth}/>}
          {(item.RangeCheck?.length || item.Validations) && <ValidationSummary validations={item.RangeCheck ?? item.Validations} dataType={item.DataType}/>}
        </div>}
      </div>
    );
  };

  const REPEAT_ACCENTS = [
    { color: NT.warn,    soft: NT.warnSoft    },
    { color: NT.logic,   soft: NT.logicSoft   },
    { color: NT.calc,    soft: NT.calcSoft    },
    { color: NT.primary, soft: NT.primarySoft },
  ];

  const renderGroup = (group, groupRef, depth = 0, isInlineRepeat = false) => {
    const groupMethOid = groupRef?.MethodOID || group.MethodOID;
    const groupMethod = groupMethOid ? methByOid[groupMethOid] : null;
    const groupCond = groupRef?.CollectionExceptionConditionOID
      ? condByOid[groupRef.CollectionExceptionConditionOID]
      : (group.ConditionOID ? condByOid[group.ConditionOID] : null);

    const subgroupByTrigger: Record<string, any[]> = {};
    const unpairedSubgroups: any[] = [];
    for (const sg of (group.Subgroups || [])) {
      if (sg.Repeating !== 'Yes') { unpairedSubgroups.push(sg); continue; }
      const sgMeth = sg.MethodOID ? methByOid[sg.MethodOID] : null;
      const trigOid = extractRepeatTriggerOid(sgMeth);
      if (trigOid && group.ItemRef.some((r: any) => r.ItemOID === trigOid)) {
        if (!subgroupByTrigger[trigOid]) subgroupByTrigger[trigOid] = [];
        subgroupByTrigger[trigOid].push(sg);
      } else {
        unpairedSubgroups.push(sg);
      }
    }

    const outerStyle = (!isInlineRepeat && depth > 0)
      ? { marginLeft: 16, paddingLeft: 14, borderLeft: `2px solid ${groupCond ? NT.logic + '60' : NT.line2}` }
      : {};
    const accent = REPEAT_ACCENTS[depth % REPEAT_ACCENTS.length];

    return (
      <div key={group.OID} style={{ marginBottom: dens.group, ...outerStyle }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
          <div style={{ fontSize: depth > 0 ? 10.5 : 11, fontWeight: 700, color: NT.ink3,
            textTransform: 'uppercase', letterSpacing: .5 }}>{group.Name}</div>
          {group.Repeating === 'Yes' && <NMChip color="warn" icon={<NI.Repeat/>}>Repeating</NMChip>}
          {groupCond && (
            <NMChip color="logic" icon={<NI.Branch/>}>{groupCond.humanReadable}</NMChip>
          )}
          <div style={{ flex: 1 }}/>
          {groupMethod && groupMethod.kind !== 'repeat' && <CalcBadge method={groupMethod}/>}
        </div>
        {group.ItemRef.map((ref: any) => {
          const item = itemByOid[ref.ItemOID];
          const paired = subgroupByTrigger[ref.ItemOID] || [];
          return (
            <Fragment key={ref.ItemOID}>
              {renderQuestion(item, ref, group.OID)}
              {paired.map((sg: any) => {
                const trigText = item?.Question?.TranslatedText?.text || ref.ItemOID;
                return (
                  <div key={sg.OID} style={{ marginTop: 6, marginLeft: 20, marginBottom: dens.gap }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4,
                      fontSize: 10.5, fontWeight: 600, color: accent.color,
                      textTransform: 'uppercase', letterSpacing: .4,
                    }}>
                      <span style={{ display: 'inline-flex', opacity: .85 }}><NI.Repeat/></span>
                      <span>Repeats for each · {trigText}</span>
                    </div>
                    <div style={{
                      border: `1.5px solid ${accent.color}55`,
                      borderRadius: 8,
                      background: accent.soft,
                      padding: '10px 12px',
                    }}>
                      {renderGroup(sg, null, depth + 1, true)}
                    </div>
                  </div>
                );
              })}
            </Fragment>
          );
        })}
        <button id="naomi-studio-button-canvas-add-question" onClick={() => onAddQuestion(group.OID)} className="nm-dashed" disabled={readonly}
          style={{ width: '100%', padding: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 6, color: NT.ink4, fontSize: 12, fontWeight: 500, marginTop: 4,
            opacity: readonly ? .5 : 1, cursor: readonly ? 'not-allowed' : 'pointer' }}>
          <NI.Plus/> Add question to {group.Name}
        </button>
        {unpairedSubgroups.map((sg: any) => renderGroup(sg, null, depth + 1))}
      </div>
    );
  };

  const [titleEditing, setTitleEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState(sdm.GlobalVariables.Name);
  useEffect(() => setTitleDraft(sdm.GlobalVariables.Name), [sdm.GlobalVariables.Name]);

  return (
    <div className="naomi-scroll" style={{ height: '100%', overflow: 'auto', background: NT.bg }}>
      <style>{`.naomi-q:hover .naomi-q-del { opacity: 1; }`}</style>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '20px 32px 80px' }}>
        <div style={{ marginBottom: 22, paddingBottom: 14, borderBottom: `1px solid ${NT.line}` }}>
          {titleEditing ? (
            <input value={titleDraft} autoFocus
              onChange={e => setTitleDraft(e.target.value)}
              onBlur={() => { onTitleEdit(titleDraft.trim() || sdm.GlobalVariables.Name); setTitleEditing(false); }}
              onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') { setTitleDraft(sdm.GlobalVariables.Name); setTitleEditing(false); } }}
              style={{ width: '100%', fontSize: 22, fontWeight: 800, color: NT.ink, letterSpacing: -0.01,
                border: `1px solid ${NT.primary}`, borderRadius: 4, padding: '4px 8px',
                background: NT.surface, fontFamily: 'inherit', outline: 'none',
                boxShadow: `0 0 0 3px ${NT.primarySoft}` }}/>
          ) : (
            <div onClick={() => setTitleEditing(true)} className="naomi-title"
              style={{ fontSize: 22, fontWeight: 800, color: NT.ink, letterSpacing: -0.01, cursor: 'text',
                padding: '4px 8px', borderRadius: 4, marginLeft: -8, transition: 'background .12s' }}
              onMouseEnter={e => e.currentTarget.style.background = NT.surface}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              {sdm.GlobalVariables.Name}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, fontSize: 12, color: NT.ink3, flexWrap: 'wrap' }}>
            <NMChip color="primary">v{tv.version} draft</NMChip>
            <span>{tv.ItemDef.length} questions · {tv.ItemGroupDef.length} sections</span>
            <span style={{ color: NT.line2 }}>·</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <NI.History/> {savedAt ? `Saved ${savedAt}` : `Last edited ${lastChanged}`}
            </span>
          </div>
        </div>

        {tv.EventDef[0].FormRef.map(fr => {
          const form = tv.FormDef.find(f => f.OID === fr.FormOID);
          if (!form) return null;
          return (
            <div key={form.OID} style={{ marginBottom: dens.group * 2 }}>
              {form.ItemGroupRef.map(gr => {
                const groupInfo = findGroupAnywhere(tv, gr.ItemGroupOID);
                if (!groupInfo) return null;
                return renderGroup(groupInfo.group, gr, 0);
              })}
            </div>
          );
        })}

        {tv.ItemDef.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: NT.ink3,
            background: NT.surface, border: `1px dashed ${NT.line2}`, borderRadius: 12 }}>
            <div style={{ display: 'inline-flex', width: 56, height: 56, borderRadius: 28,
              background: NT.primarySoft, alignItems: 'center', justifyContent: 'center', marginBottom: 12, color: NT.primary }}>
              <svg width="28" height="28" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M3 4h10M3 8h10M3 12h6"/></svg>
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: NT.ink, marginBottom: 4 }}>Start building your trade template</div>
            <div style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.5, maxWidth: 380, margin: '4px auto 16px' }}>
              Add your first section, then drop questions into it. Use the Outline panel on the left, or click the "+" inside any section.
            </div>
            <NMButton id="naomi-studio-button-canvas-add-first-question" color="primary" icon={<NI.Plus/>}
              onClick={() => onAddQuestion(tv.ItemGroupDef[0]?.OID)}>Add first question</NMButton>
          </div>
        )}
      </div>
    </div>
  );
}

interface InspectorProps {
  sdm: any;
  selectedOid: string | null;
  onUpdate: (oid: string, patch: any) => void;
  onUpdateRef: (formOid: string, groupOid: string, itemOid: string, patch: any) => void;
  onAddBranch: (oid: string, editing?: any) => void;
  onAddCalc: (oid: string) => void;
  onClearLogic: (oid: string, type: string) => void;
  onUpdateChoices: (oid: string, choices: any[]) => void;
  onUpdateImages: (images: any[]) => void;   // ← ADD THIS
  onManageUnits: () => void;
  onEnsureChoiceList: (itemOid: string) => void;
  readonly: boolean;
  onAddMapping?: () => void;
  onEditMapping?: (mapping: any) => void;
  onDeleteMapping?: (name: string) => void;
  onDeselect?: () => void;
}

function Inspector({ sdm, selectedOid, onUpdate, onUpdateRef, onAddBranch, onAddCalc, onClearLogic, onUpdateChoices, onUpdateImages, onManageUnits, onEnsureChoiceList, readonly, onAddMapping, onEditMapping, onDeleteMapping, onDeselect }: InspectorProps) {
  const tv = sdm.TemplateVersion[0];
  const item = tv.ItemDef.find(i => i.OID === selectedOid);
  const { ImageDropPicker: ImageDropPickerComp, ImageConstraintsEditor: ImageConstraintsEditorComp, ChoiceOptionImageInput: ChoiceOptionImageInputComp } = getNaomiRegistry();
  const refInfo = useMemo(() => item ? findRefInfo(tv, item.OID) : null, [item, sdm]);
  const { accessToken: inspectorToken } = useOidcAccessToken();
  const [catalogueSasMap, setCatalogueSasMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!inspectorToken) return;
    const clOid = (item as any)?.CodeListRef?.CodeListOID;
    if (!clOid) return;
    const cl = tv.CodeList?.find((c: any) => c.OID === clOid);
    if (!cl) return;
    const hasImages = cl.CodeListItem?.some((ci: any) => ci.ImageUrl || ci.ThumbnailUrl);
    if (!hasImages) return;
    getCatalogueImages(inspectorToken).then(images => {
      const map: Record<string, string> = {};
      for (const img of images) {
        const base = img.baseUrl ?? '';
        if (img.imageUrl && img.previewUrl) {
          map[img.imageUrl] = img.previewUrl;
          map[base + img.imageUrl] = img.previewUrl;
        }
        if (img.thumbnailUrl && img.thumbnailPreviewUrl) {
          map[img.thumbnailUrl] = img.thumbnailPreviewUrl;
          map[base + img.thumbnailUrl] = img.thumbnailPreviewUrl;
        }
      }
      setCatalogueSasMap(map);
      if (!tv.Images?.some((img: any) => img.baseUrl)) {
        const base = images.find(img => img.baseUrl)?.baseUrl;
        if (base) onUpdateImages([{ baseUrl: base }]);
      }
    }).catch(() => {});
  }, [item?.OID, inspectorToken]);
  const drivenGroups = useMemo(() => {
    if (!item) return [];
    const result: Array<{ group: any; parentGroup: any }> = [];
    for (const g of tv.ItemGroupDef) {
      for (const sg of (g.Subgroups || [])) {
        if (sg.Repeating !== 'Yes') continue;
        const sgMeth = sg.MethodOID ? tv.MethodDef.find((m: any) => m.OID === sg.MethodOID) : null;
        if (extractRepeatTriggerOid(sgMeth) === item.OID) {
          result.push({ group: sg, parentGroup: g });
        }
      }
    }
    return result;
  }, [item?.OID, tv]);

  if (!item) {
    const mappings: any[] = tv.Mappings || [];
    return (
      <div className="naomi-scroll" style={{ height: '100%', overflow: 'auto', padding: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: NT.ink3, textTransform: 'uppercase', letterSpacing: .6, marginBottom: 12 }}>Template Properties</div>
        <div style={{ background: NT.surface, borderRadius: 8, border: `1px solid ${NT.line}`, padding: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: NT.ink }}>{sdm.GlobalVariables.Name}</div>
          <div style={{ fontSize: 12.5, color: NT.ink3, marginTop: 4 }}>{sdm.GlobalVariables.Description}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
            <NMChip color="primary">v{tv.version} draft</NMChip>
            <NMChip>{tv.ItemDef.length} questions</NMChip>
            <NMChip>{tv.FormDef.length} forms</NMChip>
            <NMChip color="logic" icon={<NI.Branch/>}>{tv.ConditionDef.length} branches</NMChip>
            <NMChip color="calc" icon={<NI.Calc/>}>{tv.MethodDef.length} formulas</NMChip>
          </div>
        </div>

        <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${NT.line}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: NT.ink3, textTransform: 'uppercase', letterSpacing: .5,
              display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ color: NT.calc, display: 'inline-flex' }}><NI.Link/></span>
              External Mappings
            </div>
            {!readonly && onAddMapping && (
              <NMButton id="naomi-studio-button-inspector-mapping-add" size="sm" variant="text" color="primary" icon={<NI.Plus/>}
                onClick={onAddMapping}>Add</NMButton>
            )}
          </div>
          {mappings.length === 0 ? (
            <button id="naomi-studio-button-inspector-mapping-empty" className="nm-dashed"
              onClick={!readonly && onAddMapping ? onAddMapping : undefined}
              style={{ width: '100%', padding: '10px', display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: 8, color: NT.ink3, fontSize: 12,
                fontWeight: 500, borderRadius: 6, cursor: (!readonly && onAddMapping) ? 'pointer' : 'default' }}>
              <NI.Link/> Connect an external data source
            </button>
          ) : mappings.map((m: any) => {
            const fieldCount = (m.Versions?.[0]?.ItemGroupDef || [])
              .reduce((sum: number, g: any) => sum + (g.ItemDef?.length || 0), 0);
            return (
              <div key={m.Name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
                padding: '10px 12px', background: NT.calcSoft, borderRadius: 8,
                border: `1px solid ${NT.calc}22` }}>
                <span style={{ color: NT.calc, display: 'inline-flex', flexShrink: 0 }}><NI.Link/></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: NT.ink,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.Name}</div>
                  <div style={{ fontSize: 11, color: NT.ink3 }}>
                    {m.Source}{fieldCount > 0 ? ` · ${fieldCount} field${fieldCount !== 1 ? 's' : ''}` : ''}
                  </div>
                </div>
                {!readonly && onEditMapping && (
                  <button id={`naomi-studio-button-inspector-mapping-edit-${m.Name}`} className="nm-icon-btn"
                    title="Edit mapping" onClick={() => onEditMapping(m)}
                    style={{ color: NT.ink3 }}><NI.Edit/></button>
                )}
                {!readonly && onDeleteMapping && (
                  <button id={`naomi-studio-button-inspector-mapping-delete-${m.Name}`} className="nm-icon-btn"
                    title="Remove mapping" onClick={() => onDeleteMapping(m.Name)}
                    style={{ color: NT.danger }}><NI.Trash/></button>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 14, fontSize: 12, color: NT.ink3, lineHeight: 1.5 }}>
          Select a question on the canvas or in the outline to edit its prompt, type, branching rules, and calculations.
        </div>
      </div>
    );
  }

  const cond = refInfo?.ref?.CollectionExceptionConditionOID ? tv.ConditionDef.find(c => c.OID === refInfo.ref.CollectionExceptionConditionOID) : null;
  const meth = refInfo?.ref?.MethodOID ? tv.MethodDef.find(m => m.OID === refInfo.ref.MethodOID) : null;
  const cl = (item.DataType === 'singleselect' || item.DataType === 'multiselect')
    ? tv.CodeList.find(c => c.OID === item.CodeListRef?.CodeListOID) : null;

  return (
    <div className="naomi-scroll" style={{ height: '100%', overflow: 'auto' }}>
      <div style={{ padding: '10px 16px 6px', borderBottom: `1px solid ${NT.line}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          {onDeselect && (
            <button onClick={onDeselect} title="Back to template properties"
              style={{ background: 'none', border: 'none', padding: '2px 4px', cursor: 'pointer',
                color: NT.ink3, display: 'inline-flex', alignItems: 'center', borderRadius: 4,
                fontSize: 11, gap: 3, fontWeight: 500 }}
              onMouseEnter={e => (e.currentTarget.style.background = NT.line)}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
              <NI.ArrowL/> Template
            </button>
          )}
          <div style={{ fontSize: 11, fontWeight: 700, color: NT.ink3, textTransform: 'uppercase', letterSpacing: .6, marginLeft: 'auto' }}>Question</div>
        </div>
        <div style={{ fontSize: 11, color: NT.ink4, fontFamily: NT.fontMono, marginTop: 2 }}>{item.OID} · in {refInfo?.group?.Name}</div>
      </div>

      <div style={{ padding: 16, display: 'grid', gap: 14, borderBottom: `1px solid ${NT.line}` }}>
        <MTextField id="naomi-studio-input-inspector-prompt" label="Prompt" value={item.Question.TranslatedText.text}
          onChange={v => onUpdate(item.OID, { Question: { TranslatedText: { lang:'en', text: v }}})}
          multiline rows={2} disabled={readonly}/>
        <MTextField id="naomi-studio-input-inspector-helper" label="Helper text" value={item.Description || ''}
          onChange={v => onUpdate(item.OID, { Description: v || undefined })}
          placeholder="Optional — shown under the prompt to clarify" disabled={readonly}/>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <DataTypePicker value={item.DataType} onChange={v => {
            const choiceTypes = ['singleselect', 'multiselect'];
            const wasChoice = choiceTypes.includes(item.DataType);
            const isChoice = choiceTypes.includes(v);
            const patch: any = { DataType: v };
            if (wasChoice && !isChoice) patch.CodeListRef = undefined;
            onUpdate(item.OID, patch);
            if (isChoice && getNaomiRegistry().ensureChoiceList) {
              setTimeout(() => onEnsureChoiceList(item.OID), 0);
            }
          }}/>
          {(item.DataType === 'integer' || item.DataType === 'float') ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <div style={{ fontSize: 11.5, fontWeight: 600, color: NT.ink3, textTransform: 'uppercase', letterSpacing: .4 }}>Unit</div>
                <button id="naomi-studio-button-inspector-manage-units" onClick={onManageUnits} style={{ fontSize: 10.5, color: NT.primary, background: 'transparent',
                  border: 'none', cursor: 'pointer', fontWeight: 600, textTransform: 'uppercase', letterSpacing: .4,
                  fontFamily: 'inherit' }}>Manage…</button>
              </div>
              <div style={{ position: 'relative' }}>
                <select value={item.MeasurementOid || ''}
                  onChange={e => onUpdate(item.OID, { MeasurementOid: e.target.value || undefined })}
                  style={{ width: '100%', height: 38, padding: '0 32px 0 10px', borderRadius: 6,
                    border: `1px solid ${NT.line2}`, background: NT.surface, color: NT.ink, fontSize: 13.5,
                    fontFamily: 'inherit', appearance: 'none', cursor: 'pointer' }}>
                  <option value="">— None —</option>
                  {sdm.BasicDefinition.MeasurementUnit.map(m => (
                    <option key={m.OID} value={m.OID}>{m.Name} ({m.Symbol})</option>
                  ))}
                </select>
                <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: NT.ink3 }}>
                  <NI.Chev/>
                </div>
              </div>
            </div>
          ) : <div/>}
        </div>
        {item.DataType === 'feetinches' && (
          <div style={{ padding: '10px 12px', background: NT.infoSoft, borderRadius: 6, fontSize: 12, color: NT.ink2,
            border: `1px solid ${NT.info}22`, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span style={{ color: NT.info }}><NI.Ruler/></span>
            <div>Collects two paired inputs — <strong>feet</strong> and <strong>inches</strong> — stored as one measurement.</div>
          </div>
        )}
        <MTextField id="naomi-studio-input-inspector-internal-name" label="Internal name (variable)" value={item.Name}
          onChange={v => onUpdate(item.OID, { Name: v })} helper="Used in calculations and integrations" disabled={readonly}/>
        <MSwitch checked={refInfo?.ref?.Mandatory === 'Yes'}
        id="naomi-studio-switch-inspector-mandatory"
          onChange={c => onUpdateRef(refInfo!.parentOid, refInfo!.group.OID, item.OID, { Mandatory: c ? 'Yes' : 'No' })}
          label="Required answer"/>
        <MSwitch checked={refInfo?.ref?.IsVisible !== 'No'}
          id="naomi-studio-switch-inspector-visible"
          onChange={c => onUpdateRef(refInfo!.parentOid, refInfo!.group.OID, item.OID, { IsVisible: c ? undefined : 'No' })}
          label="Visible in preview"/>
        {(() => {
          const groupSummarizes =
            (refInfo?.group as any)?.SummarizeInstances === 'Yes' ||
            (tv.FormDef || []).some(f =>
              (f.ItemGroupRef || []).some(r =>
                r.ItemGroupOID === refInfo?.group?.OID && (r as any).SummarizeInstances === 'Yes'
              )
            );
          if (!groupSummarizes) return null;
          return (
            <div>
              <div style={{ marginBottom: 4 }}>
                <div style={{ fontSize: 11.5, fontWeight: 600, color: NT.ink3,
                  textTransform: 'uppercase', letterSpacing: .4 }}>Show on</div>
              </div>
              <div style={{ position: 'relative' }}>
                <select value={refInfo?.ref?.ShowOn || ''}
                  onChange={e => onUpdateRef(refInfo!.parentOid, refInfo!.group.OID, item.OID,
                    { ShowOn: e.target.value || undefined })}
                  disabled={readonly}
                  style={{ width: '100%', height: 36, padding: '0 28px 0 10px', borderRadius: 6,
                    border: `1px solid ${NT.line2}`,
                    background: NT.surface,
                    color: NT.ink,
                    fontSize: 13, fontFamily: 'inherit', appearance: 'none',
                    cursor: readonly ? 'not-allowed' : 'pointer' }}>
                  <option value="">— Default —</option>
                  <option value="Normal">Normal</option>
                  <option value="Summarize">Summarize</option>
                </select>
                <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  pointerEvents: 'none', color: NT.ink3 }}><NI.Chev/></div>
              </div>
              <div style={{ fontSize: 11, color: NT.ink4, marginTop: 3 }}>
                Normal = appears in full-detail view · Summarize = appears in the collapsed summary
              </div>
            </div>
          );
        })()}
      </div>

      {ImageDropPickerComp && (
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${NT.line}` }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: NT.ink3, textTransform: 'uppercase',
            letterSpacing: .5, marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: NT.primary }}><NI.Eye/></span>Illustration
            </span>
            {item.ImageUrl && (
              <button onClick={() => onUpdate(item.OID, { ImageUrl: undefined })}
                style={{ fontSize: 10.5, color: NT.danger, background: 'transparent',
                  border: 'none', cursor: 'pointer', fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: .4, fontFamily: 'inherit' }}>Remove</button>
            )}
          </div>
          <ImageDropPickerComp value={item.ImageUrl}
            onChange={url => onUpdate(item.OID, { ImageUrl: url || undefined })}
            label="Drop a photo to illustrate this question, click to browse, or paste URL"/>
          <div style={{ fontSize: 11, color: NT.ink4, marginTop: 6 }}>
            Shown above the prompt in Preview to help contractors recognize what's being asked.
          </div>
        </div>
      )}

      <div style={{ padding: 16, borderBottom: `1px solid ${NT.line}` }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: NT.ink3, textTransform: 'uppercase', letterSpacing: .5,
          marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: NT.info }}><NI.Validate/></span>Validation rules
        </div>
        <ValidationEditor key={item.OID} dataType={item.DataType}
          value={item.RangeCheck?.length ? rangeCheckToValidations(item.RangeCheck) : ((item as any).Validations || (item as any).Validation || {})}
          choices={cl?.CodeListItem?.map((ci: any) => ({ CodedValue: ci.CodedValue, label: ci.Decode.TranslatedText.text }))}
          onChange={v => {
            const rc = validationsToRangeCheck(v);
            onUpdate(item.OID, {
              RangeCheck: rc.length ? rc : undefined,
              Validations: rc.length ? undefined : (Object.keys(v).length ? v : undefined),
            });
          }}/>
      </div>
      {item.DataType === 'image' && ImageConstraintsEditorComp && (
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${NT.line}` }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: NT.ink3, textTransform: 'uppercase',
            letterSpacing: .5, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: NT.primary }}><NI.Eye/></span>Photo upload settings
          </div>
          <ImageConstraintsEditorComp value={item.ImageConstraints}
            onChange={c => onUpdate(item.OID, { ImageConstraints: c })}/>
        </div>
      )}
      {item.DataType === 'composite' && (
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${NT.line}` }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: NT.ink3, textTransform: 'uppercase',
            letterSpacing: .5, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: NT.primary }}><NI.Group/></span>Custom type definition
          </div>
          {item.CompositeRef && getNaomiRegistry().customTypes[item.CompositeRef] ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 10, background: NT.surfaceAlt,
              borderRadius: 6, border: `1px solid ${NT.line}`, justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: NT.ink }}>{getNaomiRegistry().customTypes[item.CompositeRef!].name}</div>
                <div style={{ fontSize: 11.5, color: NT.ink3, marginTop: 2 }}>
                  {getNaomiRegistry().customTypes[item.CompositeRef!].fields.length} fields
                </div>
              </div>
              <NMButton id="naomi-studio-button-inspector-edit-composite" size="sm" variant="outlined" color="neutral" icon={<NI.Edit/>}
                onClick={() => window.dispatchEvent(new CustomEvent('naomi-edit-composite', { detail: item.CompositeRef }))}>
                Edit fields
              </NMButton>
            </div>
          ) : (
            <NMButton id="naomi-studio-button-inspector-define-composite" size="sm" color="primary" icon={<NI.Plus/>}
              onClick={() => window.dispatchEvent(new CustomEvent('naomi-create-composite', { detail: item.OID }))}>
              Define custom type
            </NMButton>
          )}
        </div>
      )}

      {cl && (
        <div style={{ padding: 16, borderBottom: `1px solid ${NT.line}` }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: NT.ink3, textTransform: 'uppercase', letterSpacing: .5 }}>Choices</div>
            <NMButton id="naomi-studio-button-inspector-choice-add" size="sm" variant="text" icon={<NI.Plus/>}
              onClick={() => onUpdateChoices(cl.OID, [...cl.CodeListItem,
                { CodedValue: String(cl.CodeListItem.length + 1),
                  Decode: { TranslatedText: { lang: 'en', text: `Option ${String.fromCharCode(65 + cl.CodeListItem.length)}` }},
                  IsEnabled: false }])}>Add</NMButton>
          </div>
          <div style={{ display:'grid', gap: 4 }}>
            {cl.CodeListItem.map((ci, i) => (
              <div key={ci.CodedValue + i} style={{ display: 'flex', alignItems: 'center', gap: 6,
                padding: '4px 8px', background: NT.surface, border: `1px solid ${NT.line}`, borderRadius: 6 }}>
                <span style={{ width: 18, height: 18, borderRadius: 9, background: NT.surfaceAlt,
                  fontSize: 10, fontWeight: 700, color: NT.ink3, flexShrink: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{i+1}</span>
                {ChoiceOptionImageInputComp && (
                  <ChoiceOptionImageInputComp
                    value={ci._previewUrl || ''}
                    imageUrl={ci.ImageUrl || ''}
                    thumbnailPreviewUrl={catalogueSasMap[ci.ThumbnailUrl] || catalogueSasMap[ci.ImageUrl] || ci._previewUrl || ''}
                    onChange={(imgData) => {
                      if (!imgData) {
                        onUpdateChoices(cl.OID, cl.CodeListItem.map((cc, j) => j === i
                          ? { ...cc, ImageUrl: undefined, ThumbnailUrl: undefined, _previewUrl: undefined, IconUrl: undefined }
                          : cc));
                      } else {
                        onUpdateChoices(cl.OID, cl.CodeListItem.map((cc, j) => j === i
                          ? { ...cc, ImageUrl: imgData.imageUrl, ThumbnailUrl: imgData.thumbnailUrl, _previewUrl: undefined, IconUrl: undefined }
                          : cc));
                        onUpdateImages([{ baseUrl: imgData.baseUrl }]);
                      }
                    }}/>
                )}
                <input value={ci.Decode.TranslatedText.text}
                  onChange={e => onUpdateChoices(cl.OID, cl.CodeListItem.map((cc,j) => j===i
                    ? { ...cc, Decode: { TranslatedText: { lang:'en', text: e.target.value }}} : cc))}
                  style={{ flex: 1, height: 24, border: 'none', outline: 'none', background: 'transparent',
                    fontSize: 13, color: NT.ink, fontFamily: 'inherit' }}/>
                {cl.CodeListItem.length > 1 && (
                  <button id={`naomi-studio-button-inspector-choice-remove-${ci.CodedValue}`} className="nm-icon-btn"
                    onClick={() => onUpdateChoices(cl.OID, cl.CodeListItem.filter((_,j)=>j!==i))}
                    style={{ width: 22, height: 22 }}><NI.Close/></button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {drivenGroups.length > 0 && (
        <div style={{ padding: 16, borderBottom: `1px solid ${NT.line}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: NT.warn, textTransform: 'uppercase',
            letterSpacing: .5, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ display: 'inline-flex' }}><NI.Repeat/></span>
            Drives repeating group
          </div>
          {drivenGroups.map(({ group: sg, parentGroup }) => (
            <div key={sg.OID} style={{ background: NT.warnSoft, borderRadius: 8, padding: '10px 12px',
              border: `1px solid ${NT.warn}33`, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span style={{ color: NT.warn, marginTop: 1, display: 'inline-flex' }}><NI.Repeat/></span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: NT.ink }}>{sg.Name}</div>
                <div style={{ fontSize: 11, color: NT.ink3, marginTop: 1 }}>in {parentGroup.Name}</div>
                <div style={{ fontSize: 11, color: NT.ink4, marginTop: 3 }}>
                  This answer controls how many times the group repeats
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ padding: 16, borderBottom: `1px solid ${NT.line}` }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: NT.ink3, textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>Branching</div>
        {cond ? (
          <div style={{ background: NT.logicSoft, borderRadius: 8, padding: '10px 12px',
            border: `1px solid ${NT.logic}33`, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span style={{ color: NT.logic }}><NI.Branch/></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: NT.logic, textTransform: 'uppercase', letterSpacing: .5 }}>Show when</div>
              <div style={{ fontSize: 13, color: NT.ink, marginTop: 2 }}>{cond.humanReadable}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <NMButton id="naomi-studio-button-inspector-branch-edit" size="sm" variant="soft" color="primary" icon={<NI.Edit/>} onClick={() => onAddBranch(item.OID, cond)}>Edit</NMButton>
                <NMButton id="naomi-studio-button-inspector-branch-remove" size="sm" variant="text" color="danger" icon={<NI.Trash/>}
                  onClick={() => onClearLogic(item.OID, 'branch')}>Remove</NMButton>
              </div>
            </div>
          </div>
        ) : (
          <button id="naomi-studio-button-inspector-branch-add" onClick={() => onAddBranch(item.OID)} className="nm-dashed"
            style={{ width: '100%', padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 8, color: NT.ink3, fontSize: 12.5, fontWeight: 500 }}>
            <NI.Branch/> Add branching rule
          </button>
        )}
      </div>

      <div style={{ padding: 16, borderBottom: `1px solid ${NT.line}` }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: NT.ink3, textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>Calculation</div>
        {meth ? (
          <div style={{ background: NT.calcSoft, borderRadius: 8, padding: '10px 12px',
            border: `1px solid ${NT.calc}33`, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span style={{ color: NT.calc }}><NI.Calc/></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: NT.calc, textTransform: 'uppercase', letterSpacing: .5 }}>Auto-fill</div>
              <div style={{ fontSize: 13, color: NT.ink, marginTop: 2 }}>{meth.humanReadable}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <NMButton id="naomi-studio-button-inspector-calc-edit" size="sm" variant="soft" color="primary" icon={<NI.Edit/>} onClick={() => onAddCalc(item.OID)}>Edit</NMButton>
                <NMButton id="naomi-studio-button-inspector-calc-remove" size="sm" variant="text" color="danger" icon={<NI.Trash/>}
                  onClick={() => onClearLogic(item.OID, 'calc')}>Remove</NMButton>
              </div>
            </div>
          </div>
        ) : (
          <button id="naomi-studio-button-inspector-calc-add" onClick={() => onAddCalc(item.OID)} className="nm-dashed"
            style={{ width: '100%', padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 8, color: NT.ink3, fontSize: 12.5, fontWeight: 500 }}>
            <NI.Calc/> Calculate this question
          </button>
        )}
      </div>
    </div>
  );
}

function formatLastModified(d: Date | null): string {
  if (!d) return 'just now';
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  return min === 1 ? '1 min ago' : `${min} min ago`;
}

function formatRestoreTime(iso: string): string {
  try {
    const d = new Date(iso);
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 60) return 'just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return min === 1 ? '1 min ago' : `${min} min ago`;
    const hr = Math.floor(min / 60);
    return hr === 1 ? '1 hour ago' : `${hr} hours ago`;
  } catch { return 'recently'; }
}

function NaomiStudio(): JSX.Element {
  const { sdm, setSdm } = useSDMState(newBlankSDM('New Template'));
  const tv = sdm.TemplateVersion[0];
  const { accessToken, accessTokenPayload } = useOidcAccessToken();
  const authorName = [accessTokenPayload?.given_name, accessTokenPayload?.family_name].filter(Boolean).join(' ')
    || accessTokenPayload?.name
    || accessTokenPayload?.preferred_username
    || 'GMS User';
  const autosaveKey = `naomi_studio_autosave_${accessTokenPayload?.oid ?? 'unknown'}`;

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      (window as any).__naomiSdm = sdm;
    }
  }, [sdm]);

  const [selectedOid, setSelectedOid]   = useState<string | null>(null);
  const [view, setView]                 = useState('library');
  const [density, setDensity]           = useState('normal');
  const [savedAt, setSavedAt]           = useState<string | null>(null);
  const [lastModified, setLastModified] = useState<Date | null>(null);
  const isFirstSdm                      = React.useRef(true);
  const lastDraftSavedAt                = React.useRef<Date | null>(null);
  const lastPublishedAt                 = React.useRef<Date | null>(null);
  const skipNextSdmEffect               = React.useRef(false);
  const suppressNextLastModified        = React.useRef(false);
  const loadedAsDraftRef                = React.useRef(false);
  const lastSdmWasDirty                 = React.useRef(false);
  const autoSaveTimerRef                = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const cleanBaselineRef                = React.useRef<any>(null);
  const currentSdmRef                   = React.useRef<any>(sdm);
  const [saveError, setSaveError]       = useState<string | null>(null);
  const [publishToast, setPublishToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [saveInfo, setSaveInfo]         = useState<string | null>(null);
  const [tradeLogo, setTradeLogo]       = useState<string>('');
  const logoFileRef                     = useRef<HTMLInputElement | null>(null);
  const dbTradeLogoRef                  = useRef<string>('');   // logo currently persisted in trade.trade_logo

  const isDraftDirty = lastModified !== null &&
    (lastDraftSavedAt.current === null || lastModified > lastDraftSavedAt.current);
  const hasNeverBeenPublished = sdm.TemplateVersion.slice(1).length === 0;
  const isPublishDirty = hasNeverBeenPublished || (lastModified !== null &&
    (lastPublishedAt.current === null || lastModified > lastPublishedAt.current));

  useEffect(() => {
    if (!publishToast) return;
    const timer = setTimeout(() => setPublishToast(null), 4000);
    return () => clearTimeout(timer);
  }, [publishToast]);

  useEffect(() => {
    if (isFirstSdm.current) { isFirstSdm.current = false; lastSdmWasDirty.current = false; return; }
    if (skipNextSdmEffect.current) {
      skipNextSdmEffect.current = false;
      const isDraftLoad = loadedAsDraftRef.current;
      loadedAsDraftRef.current = false;
      const baseline = isDraftLoad ? new Date(0) : null;
      setLastModified(baseline);
      lastDraftSavedAt.current = baseline;
      lastPublishedAt.current = null;
      setSaveInfo(null);
      lastSdmWasDirty.current = false;
      cleanBaselineRef.current = sdm;
      return;
    }
    if (suppressNextLastModified.current) { suppressNextLastModified.current = false; lastSdmWasDirty.current = false; return; }
    lastSdmWasDirty.current = true;
    setLastModified(new Date());
    setSaveInfo(null);
  }, [sdm]);

  useEffect(() => { currentSdmRef.current = sdm; }, [sdm]);

  useEffect(() => {
    if (!lastSdmWasDirty.current) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(autosaveKey, JSON.stringify({
          sdm,
          tradeName: sdm.GlobalVariables.Name,
          savedAt: new Date().toISOString(),
          hasDirtyChanges: true,
          cleanBaseline: cleanBaselineRef.current,
        }));
      } catch (err: any) {
        console.warn('[NaomiStudio] Auto-save skipped:', err?.message);
        setSaveError('Auto-save failed (storage full). Save your draft manually to avoid data loss.');
      }
    }, 800);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [sdm]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(autosaveKey);
      if (!raw) return;
      const stored = JSON.parse(raw);
      if (stored?.hasDirtyChanges && stored?.tradeName && stored?.savedAt && stored?.sdm) {
        setRestoreBanner({ tradeName: stored.tradeName, savedAt: stored.savedAt, sdm: stored.sdm, cleanBaseline: stored.cleanBaseline ?? null });
      }
    } catch {
      localStorage.removeItem(autosaveKey);
    }
  }, []);

  useEffect(() => () => {
    isFirstSdm.current = true;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    if (!lastSdmWasDirty.current) return;
    const s = currentSdmRef.current;
    try {
      localStorage.setItem(autosaveKey, JSON.stringify({
        sdm: s,
        tradeName: s.GlobalVariables.Name,
        savedAt: new Date().toISOString(),
        hasDirtyChanges: true,
        cleanBaseline: cleanBaselineRef.current,
      }));
    } catch (err: any) {
      console.warn('[NaomiStudio] Unmount auto-save failed:', err?.message);
    }
  }, []);

  const [newQDlg, setNewQDlg]           = useState<{ open: boolean; groupOid: string | null | undefined }>({ open: false, groupOid: null });
  const [branchDlg, setBranchDlg]       = useState<{ open: boolean; target: string | null; scope: string; editing: any }>({ open: false, target: null, scope: 'question', editing: null });
  const [calcDlg, setCalcDlg]           = useState<{ open: boolean; itemOid: string | null }>({ open: false, itemOid: null });
  const [confirmDlg, setConfirmDlg]     = useState<{ title: string; body: React.ReactNode; confirmLabel?: string; onConfirm: () => void } | null>(null);
  const [publishDlg, setPublishDlg]     = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isSaving, setIsSaving]         = useState(false);
  const [renaming, setRenaming]         = useState<{ groupOid: string; current: string } | null>(null);
  const [unitsMgr, setUnitsMgr]         = useState(false);
  const [forkPicker, setForkPicker]     = useState(false);
  const [library, setLibrary]           = useState<any[]>([]);
  const [dbTrades, setDbTrades]         = useState<any[]>([]);
  const [libLoading, setLibLoading]     = useState(false);
  const [mappingTradeId, setMappingTradeId]       = useState<number | null>(null);
  const [mappingData, setMappingData]             = useState<{ sdm: any; tasks: Task[]; reloadKey: number } | null>(null);
  const [mappingLoading, setMappingLoading]       = useState(false);
  const [mappingTenants, setMappingTenants]       = useState<TenantOption[]>([]);
  const [mappingTenantsLoading, setMappingTenantsLoading] = useState(false);
  const [mappingSdm, setMappingSdm]               = useState<any>(null);   // trade's provider-level SDM (questions)
  const [mappingScope, setMappingScope]           = useState<string>('');  // LOAD source: '' = none; a tenantId or PROVIDER_SCOPE
  const [mappingSaveScopes, setMappingSaveScopes] = useState<string[]>([]);  // SAVE targets: subset of { loaded tenant, PROVIDER_SCOPE } — never other tenants
  const [mappingSaving, setMappingSaving]         = useState(false);
  const [publishResult, setPublishResult]         = useState<{ ok: boolean; message: string } | null>(null);
  const [customTypes, setCustomTypes]   = useState<Record<string, any>>(() => getNaomiRegistry().customTypes);
  const [customTypeDlg, setCustomTypeDlg] = useState<{ open: boolean; editing: any; attachToOid: string | null }>({ open: false, editing: null, attachToOid: null });
  const [repeatDlg, setRepeatDlg]       = useState<{ open: boolean; groupOid: string | null }>({ open: false, groupOid: null });
  const [bannerDlg, setBannerDlg]       = useState<{ open: boolean; groupOid: string | null }>({ open: false, groupOid: null });
  const [restoreBanner, setRestoreBanner] = useState<{ tradeName: string; savedAt: string; sdm: any; cleanBaseline: any } | null>(null);
  const [mappingDlg, setMappingDlg]     = useState<{ open: boolean; editing: any | null }>({ open: false, editing: null });

  React.useEffect(() => {
    const onCreate = (e) => setCustomTypeDlg({ open: true, editing: null, attachToOid: e.detail });
    const onEdit = (e) => setCustomTypeDlg({ open: true, editing: { id: e.detail, ...getNaomiRegistry().customTypes[e.detail] }, attachToOid: null });
    window.addEventListener('naomi-create-composite', onCreate);
    window.addEventListener('naomi-edit-composite', onEdit);
    return () => {
      window.removeEventListener('naomi-create-composite', onCreate);
      window.removeEventListener('naomi-edit-composite', onEdit);
    };
  }, []);

  const [versionContext, setVersionContext] = useState<{ version: string; author: string; date: string; note: string; readonly: boolean; changes: number } | null>(null);

  const { RichVersionsPane: RichVersionsPaneComp, RepeatingGroupConfig: RepeatingGroupConfigComp, CustomTypeBuilder: CustomTypeBuilderComp, GroupHeaderImageDialog: GroupHeaderImageDialogComp } = getNaomiRegistry();

  const updateItem = (oid, patch) => setSdm(s => ({ ...s, TemplateVersion: s.TemplateVersion.map((v, i) => i===0
    ? { ...v, ItemDef: v.ItemDef.map(it => it.OID === oid ? { ...it, ...patch } : it) } : v) }));

  const updateItemRef = (parentOid, groupOid, itemOid, patch) => setSdm(s => ({
    ...s, TemplateVersion: s.TemplateVersion.map((v, i) => i===0
      ? { ...v, ItemGroupDef: mapGroup(v, groupOid, g => ({
          ...g, ItemRef: g.ItemRef.map(r => r.ItemOID === itemOid ? { ...r, ...patch } : r) })) } : v) }));

  const updateChoices = (codeListOid, items) => setSdm(s => ({
    ...s, TemplateVersion: s.TemplateVersion.map((v, i) => i===0
      ? { ...v, CodeList: v.CodeList.map(cl => cl.OID === codeListOid ? { ...cl, CodeListItem: items } : cl) } : v) }));

  const updateImages = (images) => setSdm(s => ({
    ...s, TemplateVersion: s.TemplateVersion.map((v, i) => i===0 ? { ...v, Images: images } : v) }));

  const renameGroup = (groupOid, name) => setSdm(s => ({
    ...s, TemplateVersion: s.TemplateVersion.map((v, i) => i===0
      ? { ...v, ItemGroupDef: mapGroup(v, groupOid, g => ({ ...g, Name: name })) } : v) }));

  const addQuestion = ({ text, helper, internalName, dataType, groupOid, mandatory, unitOid, choices, imageUrl, validations }) => {
    let nextOid: string | null = null;
    setSdm(s => {
      const v = s.TemplateVersion[0];
      const nextN = Math.max(0, ...v.ItemDef.map(i => parseInt(i.OID.slice(2))||0)) + 1;
      const oid = `I.${nextN}`;
      nextOid = oid;
      const newItem: any = {
        OID: oid, Name: internalName || `Q${nextN}`, DataType: dataType,
        Question: { TranslatedText: { lang: 'en', text } },
        ...(helper ? { Description: helper } : {}),
        ...(unitOid ? { MeasurementOid: unitOid } : {}),
        ...(imageUrl ? { ImageUrl: imageUrl } : {}),
        ...(validations ? (() => { const rc = validationsToRangeCheck(validations); return rc.length ? { RangeCheck: rc } : { Validations: validations }; })() : {}),
      };
      let codeListAdd: any = null;
      if ((dataType === 'singleselect' || dataType === 'multiselect') && choices?.length) {
        const clOid = `CL.${nextN}`;
        newItem.CodeListRef = { CodeListOID: clOid };
        codeListAdd = { OID: clOid, DataType: 'text', CodeListItem: choices.map((c, idx) => {
          const txt = typeof c === 'string' ? c : (c?.text || '');
          const img  = typeof c === 'string' ? null : (c?.image || null);
          const thumb = typeof c === 'string' ? null : (c?.thumbnailUrl || null);
          return {
            CodedValue: String(idx + 1),
            Decode: { TranslatedText: { lang: 'en', text: txt }},
            IsEnabled: false,
            ...(img   ? { ImageUrl: img }     : {}),
            ...(thumb ? { ThumbnailUrl: thumb } : {}),
          };
        }) };
      }
      return { ...s, TemplateVersion: s.TemplateVersion.map((vv, idx) => idx===0 ? {
        ...vv,
        ItemDef: [...vv.ItemDef, newItem],
        CodeList: codeListAdd ? [...vv.CodeList, codeListAdd] : vv.CodeList,
        ItemGroupDef: mapGroup(vv, groupOid, g => ({
          ...g, ItemRef: [...g.ItemRef, { ItemOID: oid, Mandatory: mandatory ? 'Yes' : 'No' }] })),
      } : vv) };
    });
    if (nextOid) setSelectedOid(nextOid);
  };

  const duplicateItem = (groupOid, itemOid) => {
    setSdm(s => {
      const v = s.TemplateVersion[0];
      const src = v.ItemDef.find(i => i.OID === itemOid);
      const groupInfo = findGroupAnywhere(v, groupOid);
      const srcRef = groupInfo!.group.ItemRef.find(r => r.ItemOID === itemOid);
      const nextN = Math.max(0, ...v.ItemDef.map(i => parseInt(i.OID.slice(2))||0)) + 1;
      const oid = `I.${nextN}`;
      const dup = JSON.parse(JSON.stringify(src));
      dup.OID = oid; dup.Name = src!.Name + 'Copy';
      dup.Question = { TranslatedText: { lang: 'en', text: src!.Question.TranslatedText.text + ' (copy)' }};
      return { ...s, TemplateVersion: s.TemplateVersion.map((vv, idx) => idx===0 ? {
        ...vv, ItemDef: [...vv.ItemDef, dup],
        ItemGroupDef: mapGroup(vv, groupOid, g => {
          const i = g.ItemRef.findIndex(r => r.ItemOID === itemOid);
          const arr = g.ItemRef.slice();
          arr.splice(i + 1, 0, { ItemOID: oid, Mandatory: srcRef.Mandatory });
          return { ...g, ItemRef: arr };
        }),
      } : vv) };
    });
  };

  const move = (groupOid, itemOid, dir) => {
    setSdm(s => ({ ...s, TemplateVersion: s.TemplateVersion.map((v, idx) => idx===0 ? {
      ...v, ItemGroupDef: mapGroup(v, groupOid, g => {
        const i = g.ItemRef.findIndex(r => r.ItemOID === itemOid);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= g.ItemRef.length) return g;
        const arr = g.ItemRef.slice(); [arr[i], arr[j]] = [arr[j], arr[i]];
        return { ...g, ItemRef: arr };
      })
    } : v)}));
  };

  const reorder = (fromGroupOid, itemOid, toGroupOid, toIdx) => {
    setSdm(s => {
      const v = s.TemplateVersion[0];
      const fromInfo = findGroupAnywhere(v, fromGroupOid);
      if (!fromInfo) return s;
      const ref = fromInfo.group.ItemRef.find(r => r.ItemOID === itemOid);
      if (!ref) return s;
      let newGroups = mapGroup(v, fromGroupOid, g => ({
        ...g, ItemRef: g.ItemRef.filter(r => r.ItemOID !== itemOid) }));
      let adjustedTo = toIdx;
      if (fromGroupOid === toGroupOid) {
        const fromIdx = fromInfo.group.ItemRef.findIndex(r => r.ItemOID === itemOid);
        if (fromIdx < toIdx) adjustedTo = toIdx - 1;
      }
      const inject = g => {
        const arr = g.ItemRef.slice();
        arr.splice(adjustedTo, 0, ref);
        return { ...g, ItemRef: arr };
      };
      newGroups = newGroups.map(g => {
        if (g.OID === toGroupOid) return inject(g);
        if (g.Subgroups?.some(sg => sg.OID === toGroupOid)) {
          return { ...g, Subgroups: g.Subgroups.map(sg => sg.OID === toGroupOid ? inject(sg) : sg) };
        }
        return g;
      });
      return { ...s, TemplateVersion: s.TemplateVersion.map((vv, i) => i===0 ? { ...vv, ItemGroupDef: newGroups } : vv) };
    });
  };

  const reorderGroup = (fromParentKey, fromGroupOid, toParentKey, toIdx) => {
    setSdm(s => {
      const v = s.TemplateVersion[0];
      const isTopLevel = v.FormDef.some(f => f.OID === fromParentKey);
      if (isTopLevel) {
        const fromForm = v.FormDef.find(f => f.OID === fromParentKey);
        const fromIdx = fromForm!.ItemGroupRef.findIndex(gr => gr.ItemGroupOID === fromGroupOid);
        if (fromIdx < 0) return s;
        const ref = fromForm!.ItemGroupRef[fromIdx];
        const adjustedTo = fromParentKey === toParentKey && fromIdx < toIdx ? toIdx - 1 : toIdx;
        const newFormDef = v.FormDef.map(f => {
          let arr = f.ItemGroupRef.slice();
          if (f.OID === fromParentKey) arr = arr.filter(gr => gr.ItemGroupOID !== fromGroupOid);
          if (f.OID === toParentKey) arr.splice(Math.min(adjustedTo, arr.length), 0, ref);
          return { ...f, ItemGroupRef: arr };
        });
        return { ...s, TemplateVersion: s.TemplateVersion.map((vv, i) => i===0 ? { ...vv, FormDef: newFormDef } : vv) };
      } else {
        const fromParent = v.ItemGroupDef.find(g => g.OID === fromParentKey);
        if (!fromParent) return s;
        const fromIdx = (fromParent.Subgroups || []).findIndex(sg => sg.OID === fromGroupOid);
        if (fromIdx < 0) return s;
        const sg = fromParent.Subgroups![fromIdx];
        const adjustedTo = fromParentKey === toParentKey && fromIdx < toIdx ? toIdx - 1 : toIdx;
        const newGroupDef = v.ItemGroupDef.map(g => {
          if (g.OID !== fromParentKey && g.OID !== toParentKey) return g;
          let subs = (g.Subgroups || []).slice();
          if (g.OID === fromParentKey) subs = subs.filter(sub => sub.OID !== fromGroupOid);
          if (g.OID === toParentKey) subs.splice(Math.min(adjustedTo, subs.length), 0, sg);
          return { ...g, Subgroups: subs };
        });
        return { ...s, TemplateVersion: s.TemplateVersion.map((vv, i) => i===0 ? { ...vv, ItemGroupDef: newGroupDef } : vv) };
      }
    });
  };

  const deleteItem = (groupOid, itemOid) => {
    const item = tv.ItemDef.find(i => i.OID === itemOid);
    setConfirmDlg({
      title: `Delete "${item?.Question?.TranslatedText?.text || 'this question'}"`,
      body: 'This removes the question from the template. Any branching rules or calculations that reference it will be cleared.',
      confirmLabel: 'Delete question',
      onConfirm: () => {
        setSdm(s => ({ ...s, TemplateVersion: s.TemplateVersion.map((v, idx) => idx===0 ? {
          ...v,
          ItemDef: v.ItemDef.filter(i => i.OID !== itemOid),
          ItemGroupDef: v.ItemGroupDef.map(g => ({
            ...g, ItemRef: g.ItemRef.filter(r => r.ItemOID !== itemOid),
            Subgroups: (g.Subgroups || []).map(sg => ({ ...sg, ItemRef: sg.ItemRef.filter(r => r.ItemOID !== itemOid) })),
          })),
        } : v) }));
        if (selectedOid === itemOid) setSelectedOid(null);
      },
    });
  };

  const addTopGroup = (name) => {
    setSdm(s => {
      const v = s.TemplateVersion[0];
      const nextN = Math.max(0, ...v.ItemGroupDef.map(g => parseInt((g.OID.match(/\d+/)||['0'])[0]) || 0)) + 1;
      const oid = `IG.${nextN}`;
      const newGroup = { OID: oid, Name: name || 'New section', Repeating: 'No' as const, ItemRef: [], Subgroups: [] };
      return { ...s, TemplateVersion: s.TemplateVersion.map((vv, idx) => idx===0 ? {
        ...vv,
        ItemGroupDef: [...vv.ItemGroupDef, newGroup],
        FormDef: vv.FormDef.map(f => ({ ...f, ItemGroupRef: [...f.ItemGroupRef, { ItemGroupOID: oid }] })),
      } : vv) };
    });
  };

  const updateGroup = (groupOid, patch) => setSdm(s => ({
    ...s, TemplateVersion: s.TemplateVersion.map((v, i) => i===0
      ? { ...v, ItemGroupDef: mapGroup(v, groupOid, g => ({ ...g, ...patch })) } : v) }));

  const addSubgroup = (parentOid) => {
    setSdm(s => {
      const v = s.TemplateVersion[0];
      const allSubs: any[] = [];
      const collect = (g) => { for (const sg of (g.Subgroups || [])) { allSubs.push(sg); collect(sg); } };
      v.ItemGroupDef.forEach(collect);
      const nextN = (allSubs.length ? Math.max(...allSubs.map(sg => parseInt(sg.OID.replace(/\D/g,''))||0)) : 0) + 1;
      const oid = `SG.${nextN}`;

      let topAncestor = parentOid;
      while (true) {
        const info = findGroupAnywhere(v, topAncestor);
        if (!info || info.parentOid === null) break;
        topAncestor = info.parentOid;
      }

      return { ...s, TemplateVersion: s.TemplateVersion.map((vv, idx) => idx===0 ? {
        ...vv,
        ItemGroupDef: mapGroup(vv, parentOid, g => ({
          ...g, Subgroups: [...(g.Subgroups || []), { OID: oid, Name: 'New subsection', Repeating: 'No', ItemRef: [], Subgroups: [] }],
        })),
        FormDef: vv.FormDef.map(f =>
          f.ItemGroupRef.some(r => r.ItemGroupOID === topAncestor)
            ? { ...f, ItemGroupRef: [...f.ItemGroupRef, { ItemGroupOID: oid, Mandatory: 'No' }] }
            : f
        ),
      } : vv) };
    });
  };

  const deleteGroup = (groupOid) => {
    const groupInfo = findGroupAnywhere(tv, groupOid);
    if (!groupInfo) return;
    setConfirmDlg({
      title: `Delete section "${groupInfo.group.Name}"?`,
      body: `This removes the section${groupInfo.group.ItemRef.length ? ` and all ${groupInfo.group.ItemRef.length} questions inside it` : ''}.`,
      confirmLabel: 'Delete section',
      onConfirm: () => {
        const removedItemOids = new Set();
        const collect = g => { g.ItemRef.forEach(r => removedItemOids.add(r.ItemOID)); (g.Subgroups || []).forEach(collect); };
        collect(groupInfo.group);
        setSdm(s => ({ ...s, TemplateVersion: s.TemplateVersion.map((v, idx) => idx===0 ? {
          ...v,
          ItemDef: v.ItemDef.filter(i => !removedItemOids.has(i.OID)),
          ItemGroupDef: groupInfo.parentOid
            ? mapGroup(v, groupInfo.parentOid, g => ({ ...g, Subgroups: (g.Subgroups || []).filter(sg => sg.OID !== groupOid) }))
            : v.ItemGroupDef.filter(g => g.OID !== groupOid),
          FormDef: v.FormDef.map(f => ({ ...f, ItemGroupRef: f.ItemGroupRef.filter(gr => gr.ItemGroupOID !== groupOid) })),
        } : v) }));
      },
    });
  };

  const openBranchForGroup = (groupOid) => {
    const groupInfo = findGroupAnywhere(tv, groupOid);
    if (!groupInfo) return;
    let editing: any = null;
    for (const f of tv.FormDef) {
      const gr = f.ItemGroupRef.find(g => g.ItemGroupOID === groupOid);
      if (gr?.CollectionExceptionConditionOID) {
        const c = tv.ConditionDef.find(c => c.OID === gr.CollectionExceptionConditionOID);
        if (c) editing = c;
      }
    }
    if (!editing && groupInfo.group.ConditionOID) {
      editing = tv.ConditionDef.find(c => c.OID === groupInfo.group.ConditionOID);
    }
    setBranchDlg({ open: true, target: groupOid, scope: 'group', editing });
  };

  const saveBranch = ({ humanReadable, FormalExpression, triggerOid, op, val, expression }) => {
    const { target, scope } = branchDlg;
    setSdm(s => {
      const v = s.TemplateVersion[0];
      let conds = v.ConditionDef.slice();
      const existingForItem = scope === 'question' ? findRefInfo(v, target)?.ref?.CollectionExceptionConditionOID : null;
      const existingForGroupForm = scope === 'group'
        ? v.FormDef.flatMap(f => f.ItemGroupRef).find(gr => gr.ItemGroupOID === target)?.CollectionExceptionConditionOID
        : null;
      const groupInfo = scope === 'group' ? findGroupAnywhere(v, target) : null;
      const existingForSubgroup = scope === 'group' && groupInfo?.parentOid ? groupInfo.group.ConditionOID : null;
      let cOid = existingForItem || existingForGroupForm || existingForSubgroup;
      const condData = { humanReadable, FormalExpression, triggerOid, op, val, expression };
      if (!cOid) {
        const nextN = (conds.length ? Math.max(...conds.map(c => parseInt(c.OID.replace(/\D/g,''))||0)) : 0) + 1;
        cOid = `C.${nextN}`;
        conds.push({ OID: cOid, ...condData });
      } else {
        conds = conds.map(c => c.OID === cOid ? { ...c, ...condData } : c);
      }

      let nextV = { ...v, ConditionDef: conds };
      if (scope === 'question') {
        const ri = findRefInfo(v, target);
        nextV.ItemGroupDef = mapGroup(nextV, ri!.group.OID, g => ({
          ...g, ItemRef: g.ItemRef.map(r => r.ItemOID === target ? { ...r, CollectionExceptionConditionOID: cOid } : r) }));
      } else {
        if (groupInfo!.parentOid) {
          nextV.ItemGroupDef = mapGroup(nextV, target, g => ({ ...g, ConditionOID: cOid }));
        } else {
          nextV.FormDef = nextV.FormDef.map(f => ({ ...f,
            ItemGroupRef: f.ItemGroupRef.map(gr => gr.ItemGroupOID === target
              ? { ...gr, CollectionExceptionConditionOID: cOid } : gr) }));
        }
      }
      return { ...s, TemplateVersion: s.TemplateVersion.map((vv, i) => i===0 ? nextV : vv) };
    });
    setBranchDlg({ open: false, target: null, scope: 'question', editing: null });
  };

  const clearGroupBranch = (groupOid) => {
    setSdm(s => ({ ...s, TemplateVersion: s.TemplateVersion.map((v, i) => i===0 ? {
      ...v,
      FormDef: v.FormDef.map(f => ({ ...f, ItemGroupRef: f.ItemGroupRef.map(gr =>
        gr.ItemGroupOID === groupOid ? { ...gr, CollectionExceptionConditionOID: undefined } : gr) })),
      ItemGroupDef: mapGroup(v, groupOid, g => ({ ...g, ConditionOID: undefined })),
    } : v) }));
  };

  const saveCalc = ({ kind, humanReadable, FormalExpression }) => {
    const itemOid = calcDlg.itemOid;
    setSdm(s => {
      const v = s.TemplateVersion[0];
      const ri = findRefInfo(v, itemOid);
      let mOid = ri?.ref?.MethodOID;
      let meths = v.MethodDef.slice();
      if (!mOid) {
        const nextN = (meths.length ? Math.max(...meths.map(m => parseInt(m.OID.replace(/\D/g,''))||0)) : 0) + 1;
        mOid = `M.${nextN}`;
        meths.push({ OID: mOid, Name: `M.CALC_${nextN}`, kind, humanReadable, FormalExpression });
      } else {
        meths = meths.map(m => m.OID === mOid ? { ...m, kind, humanReadable, FormalExpression } : m);
      }
      return { ...s, TemplateVersion: s.TemplateVersion.map((vv, i) => i===0 ? {
        ...vv, MethodDef: meths,
        ItemGroupDef: mapGroup(vv, ri!.group.OID, g => ({
          ...g, ItemRef: g.ItemRef.map(r => r.ItemOID === itemOid ? { ...r, MethodOID: mOid } : r) })),
      } : vv) };
    });
    setCalcDlg({ open: false, itemOid: null });
  };

  const clearLogic = (itemOid, type) => {
    const clearInGroups = (groups) => groups.map(g => ({
      ...g,
      ItemRef: g.ItemRef.map(r => r.ItemOID === itemOid
        ? (type === 'branch' ? { ...r, CollectionExceptionConditionOID: undefined } : { ...r, MethodOID: undefined })
        : r),
      Subgroups: clearInGroups(g.Subgroups || []),
    }));
    setSdm(s => ({ ...s, TemplateVersion: s.TemplateVersion.map((v, idx) => idx===0
      ? { ...v, ItemGroupDef: clearInGroups(v.ItemGroupDef) }
      : v) }));
  };

  const updateTemplateName = (name) => setSdm(s => ({
    ...s,
    GlobalVariables: { ...s.GlobalVariables, Name: name },
    TemplateVersion: s.TemplateVersion.map((v, i) => i === 0
      ? { ...v, FormDef: v.FormDef.map(f => ({ ...f, Name: name })) }
      : v) }));

  const addToLibrary = (sdmObj) => {
    const tv2 = sdmObj.TemplateVersion[0];
    const entry = {
      trade: sdmObj.GlobalVariables.Name,
      category: 'Custom',
      items: tv2.ItemDef.length,
      version: tv2.version || (sdmObj.draft?.version || '0.1'),
      status: 'draft',
      updated: new Date().toISOString().slice(0, 10),
      color: '#6b7c93',
      _sdm: sdmObj,
    };
    setLibrary(lib => [entry, ...lib]);
  };

  const deleteFromLibrary = (trade) => {
    setConfirmDlg({
      title: 'Delete this trade template?',
      body: <span>Permanently delete <strong>{trade.trade}</strong> from the database. This cannot be undone — any in-flight bid that references this trade will keep working, but new bids won't be able to use it.</span>,
      confirmLabel: 'Delete template',
      onConfirm: async () => {
        setConfirmDlg(null);
        setSaveError(null);
        try {
          const deleteId = trade.id ?? trade.tradeOid;
          if (deleteId) {
            const res = await deleteTrade(deleteId, accessToken);
            if (!res.ok) throw new Error(res.message || 'Delete failed');
          }
          setLibrary(lib => lib.filter(t => t.trade !== trade.trade));
          loadLibraryTrades();
        } catch (err: any) {
          setSaveError(err?.message || 'Delete failed');
        }
      },
    });
  };

  const startBlankTemplate = (name) => {
    const blank = newBlankSDM(name || 'Untitled trade template');
    skipNextSdmEffect.current = true;
    dbTradeLogoRef.current = '';
    setTradeLogo('');
    setSdm(blank);
    setSelectedOid(null);
    setVersionContext(null);
    setView('studio');
    setSavedAt(null);
    addToLibrary(blank);
  };

  const isAssetTemplate = (s) =>
    (s?.GlobalVariables as any)?.tradeOid === 'A.1' ||
    s?.GlobalVariables?.TemplateType === 'Asset';

  const openAssetTemplate = async () => {
    setSaveError(null);
    dbTradeLogoRef.current = '';
    setTradeLogo('');
    try {
      const res = await loadNaomiTemplateByTradeOid('A.1', accessToken);
      if (res.ok && res.data) {
        const loadedSdm = deserializeFromSdmDocument(JSON.parse(res.data));
        (loadedSdm.GlobalVariables as any).tradeOid = 'A.1';
        if (loadedSdm.GlobalVariables.TemplateType !== 'Asset') {
          (loadedSdm.GlobalVariables as any).TemplateType = 'Asset';
        }
        skipNextSdmEffect.current = true;
        setSdm(loadedSdm); setSelectedOid(null); setVersionContext(null); setView('studio'); setSavedAt(null);
      } else {
        const blank = newAssetSDM();
        setSdm(blank); setSelectedOid(null); setVersionContext(null); setView('studio'); setSavedAt(null);
      }
    } catch (err: any) {
      setSaveError(err?.message || 'Failed to load asset template');
    }
  };

  const forkFromTrade = (trade) => {
    let forked;
    if (trade._sdm) {
      forked = forkSDM(trade._sdm, `${trade.trade} (forked)`);
    } else {
      forked = newBlankSDM(`${trade.trade} (forked)`);
      forked.GlobalVariables.Description = `Forked from ${trade.trade} v${trade.version} (placeholder schema — fill out questions to match)`;
      forked.draft.basedOn = `${trade.trade} v${trade.version}`;
    }
    cleanBaselineRef.current = forked;
    dbTradeLogoRef.current = '';
    setTradeLogo('');
    setSdm(forked);
    setSelectedOid(null);
    setVersionContext(null);
    setView('studio');
    setSavedAt(null);
  };

  const saveUnits = (units) => setSdm(s => ({
    ...s, BasicDefinition: { ...s.BasicDefinition, MeasurementUnit: units } }));

  const updateMappings = (mappings: any[]) => setSdm(s => ({
    ...s, TemplateVersion: s.TemplateVersion.map((v: any, i: number) =>
      i === 0 ? { ...v, Mappings: mappings } : v) }));

  const TRADE_COLORS = ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6','#f97316','#06b6d4'];

  const loadLibraryTrades = async () => {
    if (!accessToken) return;
    setLibLoading(true);
    try {
      const res = await getTrades(accessToken);
      if (!res?.ok || !Array.isArray(res.data)) return;

      const basicTrades = res.data.map((t, i) => ({
        trade: t.name,
        category: t.type || 'standard',
        status: 'loading',
        items: 0,
        version: 'v1',
        updated: '',
        color: TRADE_COLORS[i % TRADE_COLORS.length],
        id: t.id,
        tradeOid: t.tradeOid,
        tradeLogo: t.tradeLogo ?? '',
        _sdm: null,
      }));

      const enriched = await Promise.all(
        basicTrades.map(async (t) => {
          try {
            const loader = t.tradeOid
              ? loadNaomiTemplateByTradeOid(t.tradeOid, accessToken)
              : loadNaomiTemplate(t.trade, accessToken);
            const tplRes = await loader;
            if (!tplRes.ok || !tplRes.data) return { ...t, status: 'unknown' };
            const loadedSdm = deserializeFromSdmDocument(JSON.parse(tplRes.data));
            if (tplRes.tradeOid) (loadedSdm.GlobalVariables as any).tradeOid = tplRes.tradeOid;
            const draft = loadedSdm.TemplateVersion[0];
            const publishedVersions = loadedSdm.TemplateVersion.slice(1);
            const latestPub = publishedVersions[publishedVersions.length - 1];
            const templateStatus = tplRes.status === 'publish' ? 'published' : 'draft';
            return {
              ...t,
              items: draft.ItemDef.length,
              version: latestPub?.version ?? draft.version,
              status: templateStatus,
              _sdm: loadedSdm,
            };
          } catch {
            return { ...t, status: 'unknown' };
          }
        })
      );
      setDbTrades(enriched);
    } finally {
      setLibLoading(false);
    }
  };

  useEffect(() => { loadLibraryTrades(); }, [accessToken]);

  const openTrade = async (t) => {
    if (t._sdm) {
      loadedAsDraftRef.current = t.status === 'draft';
      skipNextSdmEffect.current = true;
      dbTradeLogoRef.current = t.tradeLogo ?? '';
      setTradeLogo(t.tradeLogo ?? '');
      setSdm(t._sdm); setSelectedOid(null); setVersionContext(null); setView('studio'); setSavedAt(null);
    } else if (t.tradeOid || t.trade) {
      setSaveError(null);
      try {
        const res = await loadNaomiTemplate(t.trade, accessToken);
        if (!res.ok) throw new Error(res.message || 'Failed to load template');
        const loadedSdm = deserializeFromSdmDocument(JSON.parse(res.data!));
        if (res.tradeOid) (loadedSdm.GlobalVariables as any).tradeOid = res.tradeOid;
        loadedAsDraftRef.current = res.status === 'draft';
        skipNextSdmEffect.current = true;
        dbTradeLogoRef.current = res.tradeLogo ?? '';
        setTradeLogo(res.tradeLogo ?? '');
        setSdm(loadedSdm); setSelectedOid(null); setVersionContext(null); setView('studio'); setSavedAt(null);
      } catch (err: any) {
        setSaveError(err?.message || 'Failed to load template');
      }
    } else {
      forkFromTrade(t);
    }
  };

  // Single "Publish" action: validate JSON, then publish BOTH the question template
  // (existing SDM API) and the task template (versioned save → provider master + tenants).
  const handlePublishAll = async () => {
    if (mappingSaving || mappingTradeId == null || !mappingData || mappingSaveScopes.length === 0) return;
    const trade = dbTrades.find((t: any) => t.id === mappingTradeId);
    const updatedBy = (accessTokenPayload as any)?.emails?.[0]
      ?? accessTokenPayload?.preferred_username
      ?? authorName
      ?? 'PROVIDER';
    // Use the up-to-date mapped task template (MetaKey stamps live in MappingPane's
    // state, mirrored to sessionStorage by syncToSession); fall back to the initial load.
    const tradeOid: string | undefined = (mappingData.sdm as any)?.GlobalVariables?.tradeOid;
    const syncedTasks = tradeOid ? sessionStorage.getItem(`naomi_task_template_${tradeOid}`) : null;
    // Fallback (no links touched this session): emit the stored shape — the original task
    // objects with all fields preserved, never the UI-only fields (id/taskNumber/_raw).
    const tasksJson = syncedTasks ?? JSON.stringify(tasksToStorage(mappingData.tasks ?? []));

    // 1. Validate JSON structure BEFORE any save — abort entirely if either is invalid.
    const taskCheck = validateTaskTemplateJson(tasksJson);
    if (!taskCheck.ok) {
      setPublishResult({ ok: false, message: `Task template invalid — ${taskCheck.error}. Nothing published.` });
      return;
    }
    const sdmCheck = validateSdmStructure(mappingData.sdm);
    if (!sdmCheck.ok) {
      setPublishResult({ ok: false, message: `Question template invalid — ${sdmCheck.error}. Nothing published.` });
      return;
    }

    // Canonical trade name comes from the trade record (resolved via trade OID), NOT from
    // the template's internal GlobalVariables.Name — the loaded SDM may carry a stale/wrong
    // name (e.g. a manually-enabled row). For P.52 this is "Roof Enhancement".
    const canonicalName = trade?.trade ?? `Trade ${mappingTradeId}`;

    setMappingSaving(true);
    setPublishResult(null);
    try {
      // 2. Publish the question template via the existing SDM builder API.
      //    Apply the mapping links FIRST — this generates the link-derived ItemDefs +
      //    MethodDefs into the SDM (e.g. derived metakey items), exactly like syncToSession.
      //    Without this step the mapping additions are missing from the payload.
      //    Then override the name from the trade OID (not the template's internal name).
      const mappingLinks = ((mappingData.sdm as any)?.TemplateVersion?.[0]?.LinkDef ?? []) as LinkDef[];
      const mappedSdm = applyLinksToSdm(mappingData.sdm as any, mappingLinks) as any;
      // serializeToSdmDocument DROPS TemplateVersion entries with status === 'draft'.
      // TemplateVersion[0] is the live draft that applyLinksToSdm just added the derived
      // items to — so keep ONLY it, with its status cleared (exactly like syncToSession).
      // Otherwise the serializer emits the prior published version and the mapping additions
      // (derived metakey items + MethodDefs) are silently lost.
      const draftTv = mappedSdm.TemplateVersion?.[0];
      const sdmForPublish = {
        ...mappedSdm,
        ...(draftTv ? { TemplateVersion: [{ ...draftTv, status: undefined }] } : {}),
        GlobalVariables: {
          ...mappedSdm.GlobalVariables,
          Name: canonicalName,
          tradeName: canonicalName,
          ProtocolName: canonicalName,
        },
      };
      const qRes = await saveNaomiTemplate(sdmForPublish as any, 'publish', authorName, accessToken);
      if (!qRes.ok) throw new Error(`Question template publish failed: ${qRes.message ?? 'unknown error'}`);

      // 3. Publish the task template (versioned) to EACH chosen SAVE target — the loaded tenant itself,
      //    and/or the provider default (with warning). A tenant's template may be saved up to the
      //    provider, but never across to another tenant, so targets are only ever {loaded tenant,
      //    provider}. Each target is an independent versioned save; a failure on one does not roll back
      //    the others (nor the already-published questions) — partial outcomes are reported.
      const labelFor = (tenantId: string | null) =>
        tenantId ? (mappingTenants.find(t => t.tenantId === tenantId)?.name ?? tenantId) : 'the provider default';
      const saved: string[] = [];
      const failures: string[] = [];
      for (const scope of mappingSaveScopes) {
        const scopeTenantId = scope === PROVIDER_SCOPE ? null : scope;
        const tRes = await saveTaskTemplateToScope(mappingTradeId, scopeTenantId, {
          name: canonicalName,
          tasks: tasksJson,
          updatedBy,
        }, accessToken);
        if (tRes?.ok) saved.push(labelFor(scopeTenantId));
        else failures.push(`${labelFor(scopeTenantId)} (${tRes?.message ?? 'unknown error'})`);
      }

      if (failures.length === 0) {
        setPublishResult({
          ok: true,
          message: `Published the question template, and saved the task template to ${saved.join(' and ')}.`,
        });
      } else {
        setPublishResult({
          ok: false,
          message: `Published the question template. Task template saved to ${saved.length ? saved.join(' and ') : 'no targets'}, but failed for ${failures.join('; ')}.`,
        });
      }
    } catch (e: any) {
      setPublishResult({ ok: false, message: `Publish failed: ${e?.message ?? 'unknown error'}` });
    } finally {
      setMappingSaving(false);
    }
  };

  // Fetch tenants that have the given trade (read-only); returns the list so the caller can default the scope.
  const loadTenantsForTrade = async (tradeId: number): Promise<TenantOption[]> => {
    setMappingTenantsLoading(true);
    try {
      const list = await getTenantsByTrade(tradeId, accessToken);
      setMappingTenants(list);
      return list;
    } catch (e: any) {
      setPublishResult({ ok: false, message: `Failed to load tenants for this trade: ${e?.message ?? 'unknown error'}` });
      setMappingTenants([]);
      return [];
    } finally {
      setMappingTenantsLoading(false);
    }
  };

  // Load the LATEST published task template for the chosen scope (a tenantId, or PROVIDER_SCOPE) into
  // the mapping. Questions/SDM are preserved (provider-level) — only the task column changes per scope.
  const loadScopeTasks = async (tradeId: number, scope: string, sdm?: any) => {
    setMappingLoading(true);
    try {
      const tenantId = scope === PROVIDER_SCOPE ? null : scope;
      const tasks = await getTaskTemplateForScope(tradeId, tenantId, accessToken);
      setMappingData(prev => ({ sdm: sdm ?? prev?.sdm ?? mappingSdm, tasks, reloadKey: (prev?.reloadKey ?? -1) + 1 }));
    } catch (e: any) {
      setPublishResult({ ok: false, message: `Failed to load the ${scope === PROVIDER_SCOPE ? 'provider' : 'tenant'} task template: ${e?.message ?? 'unknown error'}` });
    } finally {
      setMappingLoading(false);
    }
  };

  // Trade selected → load the (provider-level) questions + the trade's tenants ONLY. The task
  // template is NOT auto-loaded: the user must explicitly pick a scope (a tenant, or Provider default)
  // from the dropdown, and only then is that scope's latest published version loaded. This prevents
  // silently showing an arbitrary tenant's (or the provider's) tasks before the user has chosen one.
  const loadMappingData = async (tradeId: number, tradeOid: string) => {
    setMappingLoading(true);
    setMappingTradeId(tradeId);
    setMappingData(null); // gate: no mapping until a scope's tasks load
    setMappingScope('');
    setMappingSaveScopes([]);
    try {
      // loadTenantsForTrade populates mappingTenants internally; its return value is unused here.
      const [sdmRes] = await Promise.all([
        loadNaomiTemplateByTradeOid(tradeOid, accessToken),
        loadTenantsForTrade(tradeId),
      ]);
      let loadedSdm: any = null;
      if (sdmRes.ok && sdmRes.data) {
        loadedSdm = deserializeFromSdmDocument(JSON.parse(sdmRes.data));
        if (sdmRes.tradeOid) (loadedSdm.GlobalVariables as any).tradeOid = sdmRes.tradeOid;
      }
      setMappingSdm(loadedSdm);
      // Intentionally do NOT set a scope or load tasks here — wait for an explicit user pick.
    } catch (e: any) {
      setPublishResult({ ok: false, message: `Failed to load the mapping data for this trade: ${e?.message ?? 'unknown error'}` });
    } finally {
      setMappingLoading(false);
    }
  };

  // Auto-load when the Mapping tab is first opened — use the trade already loaded in Build tab
  useEffect(() => {
    if (view !== 'mapping') return;
    if (mappingTradeId !== null) return;

    // Remove stale 'unknown' keys left by previous sessions
    sessionStorage.removeItem('naomi_question_template_unknown');
    sessionStorage.removeItem('naomi_task_template_unknown');

    const tradeOid = (sdm?.GlobalVariables as any)?.tradeOid as string | undefined;
    const trade = dbTrades.find((t: any) => t.tradeOid === tradeOid);
    if (trade?.id && trade.tradeOid) {
      loadMappingData(trade.id, trade.tradeOid);
    } else {
      // No trade resolved — seed SDM only (no tasks). Do NOT sync to sessionStorage
      // until a proper trade is selected via the dropdown.
      setMappingData(prev => ({ sdm, tasks: [], reloadKey: (prev?.reloadKey ?? -1) + 1 }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const openVersion = (entry) => {
    const snap = snapshotSDM(sdm, entry.version);
    setVersionContext({
      version: entry.version,
      author: entry.author,
      date: entry.date,
      note: entry.note,
      readonly: entry.status !== 'draft',
      changes: entry.changes,
    });
    setView('studio');
    setSelectedOid(null);
  };

  const exitVersion = () => {
    setVersionContext(null);
  };

  const handleLogoFile = async (file?: File) => {
    if (!file) return;
    try {
      setTradeLogo(await fileToLogoDataUrl(file));
    } catch (err: any) {
      setPublishToast({ type: 'error', message: err?.message || 'Could not read that image.' });
    }
  };

  const syncTradeLogoToDb = async (tradeOid?: string): Promise<boolean> => {
    if (!tradeOid || tradeLogo === dbTradeLogoRef.current) return true;
    try {
      const res = await getTrades(accessToken);
      if (!res?.ok || !Array.isArray(res.data)) throw new Error('Unable to load the trade record.');
      const trade = res.data.find((t: any) => t.tradeOid === tradeOid);
      if (!trade) throw new Error('Trade record not found.');
      const upd = await updateTrade(trade.id, { ...trade, tradeLogo }, accessToken);
      if (!upd?.ok || upd.data !== true) throw new Error(upd?.message || 'Failed to save the trade logo.');
      dbTradeLogoRef.current = tradeLogo;
      return true;
    } catch {
      return false;
    }
  };

  const saveDraft = async () => {
    if (!isDraftDirty) { setSaveInfo(`There are no changes on "${sdm.GlobalVariables.Name}".`); return; }
    setSaveError(null);
    setIsSaving(true);
    try {
      const saveFn = isAssetTemplate(sdm) ? saveNaomiAssetTemplate : saveNaomiTemplate;
      const result = await saveFn(sdm, 'draft', authorName, accessToken);
      if (!result.ok) throw new Error(result.message || 'Save failed');
      if (result.tradeOid) {
        suppressNextLastModified.current = true;
        setSdm(s => ({ ...s, GlobalVariables: { ...s.GlobalVariables, tradeOid: result.tradeOid, tradeName: s.GlobalVariables.Name } as any }));
      }
      cleanBaselineRef.current = JSON.parse(JSON.stringify(sdm));
      const now = new Date();
      lastDraftSavedAt.current = now;
      setSavedAt(now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      localStorage.removeItem(autosaveKey);
      loadLibraryTrades();
      setPublishToast({ type: 'success', message: `"${sdm.GlobalVariables.Name}" draft saved successfully.` });
    } catch (err: any) {
      setPublishToast({ type: 'error', message: err?.message || 'Save failed' });
    } finally {
      setIsSaving(false);
    }
  };

  const publish = async ({ version, note }) => {
    setSaveError(null);

    const publishMeta = {
      author: authorName,
      date: new Date().toISOString().slice(0, 10),
      note: note || 'Published from draft',
    };

    const buildNewPublished = (draft, published) => {
      const pCount = published.length;
      const overwriteIdx = published.findIndex(p => p.version === version);
      if (overwriteIdx >= 0) {
      const existing = published[overwriteIdx];
      const fullSnapshot = { ...draft, Images: draft.Images?.length ? draft.Images : existing?.Images, version, status: 'published', _meta: publishMeta };        return published.map((p, i) => i === overwriteIdx ? fullSnapshot : p);
      } else {
        const resolvedBase = resolveVersionChain(published);
        const deltaContent = resolvedBase ? computeDelta(draft, resolvedBase) : { ...draft };
        const prevOid = pCount > 0 ? `MDV.${pCount}` : undefined;
        return [
          ...published,
          { ...deltaContent, version, status: 'published', _meta: publishMeta, ...(prevOid ? { IncludeOID: prevOid } : {}) },
        ];
      }
    };

    const draft    = sdm.TemplateVersion[0];
    const published = sdm.TemplateVersion.slice(1);
    const newPublished = buildNewPublished(draft, published);

    const publishedSdm = {
      ...sdm,
      TemplateVersion: [draft, ...newPublished],
    };

    const prePublishSnapshot = sdm;

    setPublishDlg(false);
    setIsPublishing(true);

    try {
      const saveFn = isAssetTemplate(sdm) ? saveNaomiAssetTemplate : saveNaomiTemplate;
      const result = await saveFn(publishedSdm, 'publish', authorName, accessToken);
      if (!result.ok) throw new Error(result.message || 'Unable to publish template. Please try again.');
      const logoSynced = isAssetTemplate(sdm)
        ? true
        : await syncTradeLogoToDb(result.tradeOid || (sdm.GlobalVariables as any).tradeOid);
      suppressNextLastModified.current = true;
      setSdm(s => {
        const sDraft    = s.TemplateVersion[0];
        const sPublished = s.TemplateVersion.slice(1);
        const newPub    = buildNewPublished(sDraft, sPublished);
        const updatedDraft = { ...sDraft, version, status: 'draft' };
        const isOverwrite = (s.history || []).some(h => h.version === version);
        const newHistoryEntry = {
          version, status: 'published' as const,
          author: publishMeta.author,
          date: publishMeta.date,
          note: publishMeta.note,
          changes: s.draft?.changedSince?.length || 0,
          questionCount: s.TemplateVersion[0].ItemDef.length,
          sectionCount: s.TemplateVersion[0].ItemGroupDef.length,
        };
        const updatedHistory = isOverwrite
          ? (s.history || []).map(h =>
              h.version === version
                ? { ...newHistoryEntry, status: 'published' as const }
                : h.status === 'published' ? { ...h, status: 'archived' as const } : h)
          : [newHistoryEntry, ...(s.history || []).map(h => h.status === 'published' ? { ...h, status: 'archived' as const } : h)];
        const next = {
          ...s,
          ...(result.tradeOid ? { GlobalVariables: { ...s.GlobalVariables, tradeOid: result.tradeOid, tradeName: s.GlobalVariables.Name } as any } : {}),
          TemplateVersion: [updatedDraft, ...newPub],
          history: updatedHistory,
          draft: { ...(s.draft || {}), version: bumpMinor(version), basedOn: version, changedSince: [] },
        };
        cleanBaselineRef.current = next;
        return next;
      });
      const publishedAt = new Date();
      lastPublishedAt.current = publishedAt;
      setSavedAt(publishedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      localStorage.removeItem(autosaveKey);
      loadLibraryTrades();
      setPublishToast(logoSynced
        ? { type: 'success', message: `"${sdm.GlobalVariables.Name}" published successfully.` }
        : { type: 'error', message: `"${sdm.GlobalVariables.Name}" was published, but the trade logo could not be saved. Publish again to retry.` });
    } catch (err: any) {
      setSdm(prePublishSnapshot);
      setPublishToast({ type: 'error', message: err?.message || 'Unable to publish template. Please try again.' });
    } finally {
      setIsPublishing(false);
    }
  };

  function bumpMinor(v) {
    const parts = (v || '0.0').split('.').map(n => parseInt(n)||0);
    return `${parts[0]}.${(parts[1]||0)+1}`;
  }

  const handleRestoreDraft = () => {
    const sdmToRestore = restoreBanner?.sdm;
    if (!sdmToRestore) { setRestoreBanner(null); return; }
    cleanBaselineRef.current = restoreBanner.cleanBaseline ?? null;
    setSdm(sdmToRestore);
    setSelectedOid(null);
    setVersionContext(null);
    setView('studio');
    setSavedAt(null);
    setRestoreBanner(null);
  };

  const handleDiscardDraft = () => {
    localStorage.removeItem(autosaveKey);
    setRestoreBanner(null);
  };

  const handleCancel = () => {
    const isNewUnsaved = !(sdm.GlobalVariables as any).tradeOid;
    if (isNewUnsaved) {
      setConfirmDlg({
        title: `Discard "${sdm.GlobalVariables.Name}"?`,
        body: 'This template has not been saved yet. All changes will be lost.',
        confirmLabel: 'Discard',
        onConfirm: () => {
          localStorage.removeItem(autosaveKey);
          skipNextSdmEffect.current = true;
          setSdm(newBlankSDM('New Template'));
          cleanBaselineRef.current = null;
          setSelectedOid(null);
          setVersionContext(null);
          setSavedAt(null);
          setView('library');
          setConfirmDlg(null);
        },
      });
      return;
    }
    if (cleanBaselineRef.current === null) {
      setConfirmDlg({
        title: 'Cannot safely discard changes',
        body: 'The saved version of this template could not be restored. To avoid losing your work, reload the template from the library instead.',
        confirmLabel: 'Go to Library',
        onConfirm: () => {
          localStorage.removeItem(autosaveKey);
          skipNextSdmEffect.current = true;
          setSdm(newBlankSDM('New Template'));
          cleanBaselineRef.current = null;
          setSelectedOid(null);
          setVersionContext(null);
          setSavedAt(null);
          setView('library');
          setConfirmDlg(null);
        },
      });
      return;
    }
    setConfirmDlg({
      title: `Discard changes to "${sdm.GlobalVariables.Name}"?`,
      body: 'All unsaved edits will be lost. The template will revert to the last saved version.',
      confirmLabel: 'Discard changes',
      onConfirm: () => {
        skipNextSdmEffect.current = true;
        setSdm(cleanBaselineRef.current);
        setSelectedOid(null);
        setVersionContext(null);
        setSavedAt(null);
        localStorage.removeItem(autosaveKey);
        setConfirmDlg(null);
      },
    });
  };

  return (
    <div className="naomi" style={{ height: '100%', display: 'flex', flexDirection: 'column', background: NT.bg }}>
      {/* App Bar */}
      <div style={{ height: 56, display: 'flex', alignItems: 'center', gap: 14,
        padding: '0 18px', borderBottom: `1px solid ${NT.line}`, background: NT.surface, flexShrink: 0,
        boxShadow: NT.shadow1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: NT.primary, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15,
            boxShadow: '0 2px 6px rgba(192,97,58,.3)' }}>N</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: NT.ink, lineHeight: 1.1, display: 'flex', alignItems: 'center', gap: 6 }}>
              NAOMI Studio
              <NMChip color="primary" style={{ height: 18, padding: '0 6px', fontSize: 10 }}>v{tv.version}d</NMChip>
            </div>
            <div style={{ fontSize: 11, color: NT.ink3, lineHeight: 1.2, marginTop: 1 }}>
              {sdm.GlobalVariables.Name} · {tv.ItemDef.length} questions
            </div>
          </div>
        </div>

        <div style={{ flex: 1 }}/>

        <div style={{ display: 'flex', background: NT.surfaceAlt, borderRadius: 8, padding: 2 }}>
          {[
            { id: 'studio',    label: 'Build',     icon: <NI.Edit/> },
            { id: 'mapping',   label: 'Mapping',   icon: <NI.Link/> },
            { id: 'preview',   label: 'Preview',   icon: <NI.Eye/> },
            { id: 'catalogue', label: 'Catalogue', icon: <NI.Camera/> },
            { id: 'versions',  label: 'Versions',  icon: <NI.History/> },
            { id: 'library',   label: 'Library',   icon: <NI.Book/> },
          ].filter(t => t.id !== 'mapping' || FEATURE_FLAGS.naomi_mapping_enable).map(t => {
            const sel = view === t.id;
            const handleTabClick = async () => {
              if (t.id === 'studio') {
                const isBlank = !(sdm.GlobalVariables as any).tradeOid &&
                  (sdm.TemplateVersion[0]?.ItemDef?.length ?? 0) === 0;
                if (isBlank) {
                  const allTrades = [
                    ...library,
                    ...dbTrades.filter(dt => !library.some(l => l.trade === dt.trade)),
                  ];
                  const first = allTrades[0];
                  if (first) {
                    await openTrade(first);
                    return;
                  }
                }
              }
              setView(t.id);
            };
            return <button id={`naomi-studio-tab-view-${t.id}`} key={t.id} onClick={handleTabClick}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                border: 'none', borderRadius: 6, cursor: 'pointer',
                background: sel ? NT.surface : 'transparent',
                color: sel ? NT.ink : NT.ink3,
                fontWeight: sel ? 600 : 500, fontSize: 12.5,
                boxShadow: sel ? NT.shadow1 : 'none',
                transition: 'all .12s' }}>
              {t.icon}{t.label}
            </button>;
          })}
        </div>

        <div style={{ width: 1, height: 24, background: NT.line, margin: '0 4px' }}/>

        {view === 'studio' && isDraftDirty && !versionContext?.readonly && (
          <NMButton id="naomi-studio-button-cancel" size="sm" color="danger"
            onClick={handleCancel}>Cancel</NMButton>
        )}
        <NMButton id="naomi-studio-button-save" size="sm" variant="outlined" color="neutral" icon={<NI.Save/>}
          disabled={isSaving || isPublishing}
          onClick={saveDraft}>{isSaving ? 'Saving…' : savedAt ? `Saved ${savedAt}` : 'Save draft'}</NMButton>
        <NMButton id="naomi-studio-button-publish" size="sm" color="primary" icon={<NI.Rocket/>}
          disabled={isPublishing}
          onClick={() => {
            const isLogoDirty = tradeLogo !== dbTradeLogoRef.current;
            if (!isPublishDirty && !isLogoDirty) { setSaveInfo(`There are no changes on "${sdm.GlobalVariables.Name}".`); return; }
            setPublishDlg(true);
          }}>{isPublishing ? 'Publishing…' : 'Publish'}</NMButton>
      </div>

      {/* Restore auto-saved draft banner */}
      {restoreBanner && (
        <div style={{ padding: '8px 18px', background: NT.primarySoft,
          borderBottom: `1px solid ${NT.primary}33`,
          display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <span style={{ color: NT.primary, display: 'inline-flex' }}><NI.History/></span>
          <span style={{ fontSize: 13, color: NT.ink2, flex: 1 }}>
            You have unsaved in-progress work on <strong>"{restoreBanner.tradeName}"</strong> — auto-saved {formatRestoreTime(restoreBanner.savedAt)}.
          </span>
          <NMButton id="naomi-studio-button-restore-draft" size="sm" color="primary"
            onClick={handleRestoreDraft}>Resume Draft</NMButton>
          <button id="naomi-studio-button-discard-draft" onClick={handleDiscardDraft}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer',
              color: NT.ink4, fontSize: 13, padding: '4px 8px', borderRadius: 4,
              fontFamily: 'inherit', fontWeight: 500 }}>
            Discard Draft
          </button>
        </div>
      )}

      {/* Save/publish error banner */}
      {saveError && (
        <div style={{ padding: '8px 18px', background: '#fff1f0', borderBottom: `1px solid #ffccc7`,
          display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{ color: NT.danger, fontSize: 13 }}>{saveError}</span>
          <button id="naomi-studio-button-close-save-error" onClick={() => setSaveError(null)} style={{ marginLeft: 'auto', border: 'none',
            background: 'transparent', color: NT.ink4, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* Body */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {versionContext && view === 'studio' && (
          <div style={{ padding: '10px 18px', background: NT.warnSoft, borderBottom: `1px solid ${NT.warn}33`,
            display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            <span style={{ color: NT.warn }}><NI.Lock/></span>
            <div style={{ flex: 1, fontSize: 12.5, color: NT.ink2 }}>
              Viewing snapshot of <strong>v{versionContext.version}</strong> by {versionContext.author} on {versionContext.date}.
              {versionContext.readonly ? ' Read-only — fork to make edits.' : ''}
            </div>
            <NMButton id="naomi-studio-button-version-context-fork" size="sm" variant="outlined" color="neutral" icon={<NI.Fork/>}
              onClick={() => {
                const fork = forkSDM(sdm, sdm.GlobalVariables.Name + ' (from v' + versionContext.version + ')');
                setSdm(fork);
                setVersionContext(null);
              }}>Fork this version</NMButton>
            <NMButton id="naomi-studio-button-version-context-back" size="sm" variant="text" color="neutral" icon={<NI.ArrowL/>}
              onClick={() => { exitVersion(); setView('versions'); }}>Back to history</NMButton>
          </div>
        )}
        <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {view === 'studio' && (
          <Fragment>
            <div style={{ width: 300, borderRight: `1px solid ${NT.line}`, background: NT.surface,
              display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <OutlineTree sdm={sdm} selectedOid={selectedOid} onSelect={setSelectedOid}
                onAddQuestion={(groupOid) => setNewQDlg({ open: true, groupOid: groupOid || tv.ItemGroupDef[0]?.OID })}
                onAddSubgroup={addSubgroup}
                onAddTopGroup={() => addTopGroup('New section')}
                onRenameGroup={(groupOid) => {
                  const gi = findGroupAnywhere(tv, groupOid);
                  setRenaming({ groupOid, current: gi?.group.Name || '' });
                }}
                onDeleteGroup={deleteGroup}
                onAddBranchToGroup={openBranchForGroup}
                onClearGroupBranch={clearGroupBranch}
                onSetGroupBanner={(groupOid) => setBannerDlg({ open: true, groupOid })}
                onDuplicate={duplicateItem}
                onDelete={deleteItem}
                onMoveUp={(g,i) => move(g,i,-1)}
                onMoveDown={(g,i) => move(g,i,+1)}
                onReorder={reorder}
                onReorderGroup={reorderGroup}
                onConfigureRepeating={(groupOid) => setRepeatDlg({ open: true, groupOid })}
                onTradeLogo={isAssetTemplate(sdm) ? undefined : () => logoFileRef.current?.click()}
                tradeLogo={tradeLogo}/>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <FormCanvas sdm={sdm} selectedOid={selectedOid} onSelect={setSelectedOid} density={density}
                onAddQuestion={(groupOid) => setNewQDlg({ open: true, groupOid })}
                onDeleteQuestion={deleteItem}
                onAddBranchToGroup={openBranchForGroup}
                onClearGroupBranch={clearGroupBranch}
                onConfigureRepeating={(groupOid) => setRepeatDlg({ open: true, groupOid })}
                readonly={!!versionContext?.readonly}
                onTitleEdit={updateTemplateName}
                savedAt={savedAt} lastChanged={formatLastModified(lastModified)}/>
            </div>
            <div style={{ width: 360, borderLeft: `1px solid ${NT.line}`, background: NT.surface,
              display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Inspector sdm={sdm} selectedOid={selectedOid}
                onUpdate={updateItem} onUpdateRef={updateItemRef}
                onUpdateChoices={updateChoices}
                onUpdateImages={updateImages}
                onManageUnits={() => setUnitsMgr(true)}
                onAddBranch={(oid, editing) => setBranchDlg({ open: true, target: oid, scope: 'question', editing: editing || null })}
                onAddCalc={(oid) => setCalcDlg({ open: true, itemOid: oid })}
                onClearLogic={clearLogic}
                onEnsureChoiceList={(oid) => setSdm(s => getNaomiRegistry().ensureChoiceList?.(s, oid) ?? s)}
                readonly={!!versionContext?.readonly}
                onAddMapping={() => setMappingDlg({ open: true, editing: null })}
                onEditMapping={(m) => setMappingDlg({ open: true, editing: m })}
                onDeleteMapping={(name) => setSdm(s => {
                  const mappings = (s.TemplateVersion[0]?.Mappings || []).filter((m: any) => m.Name !== name);
                  return { ...s, TemplateVersion: s.TemplateVersion.map((v: any, i: number) => i === 0 ? { ...v, Mappings: mappings } : v) };
                })}
                onDeselect={() => setSelectedOid(null)}/>
            </div>
          </Fragment>
        )}
        {view === 'mapping' && FEATURE_FLAGS.naomi_mapping_enable && (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <MappingTradeSelector
                trades={dbTrades
                  .filter((t: any) => t.id && t.tradeOid)
                  .map((t: any) => ({ id: t.id, tradeOid: t.tradeOid, name: t.trade }))}
                selectedTradeId={mappingTradeId}
                questionCount={(mappingData?.sdm ?? mappingSdm)?.TemplateVersion?.[0]?.ItemDef?.length ?? 0}
                taskCount={mappingData?.tasks.length ?? 0}
                loading={mappingLoading}
                onChange={(id, oid) => loadMappingData(id, oid)}
              />
              {mappingTradeId !== null && (
                <MappingScopeSelect
                  label="Load from"
                  tenants={mappingTenants}
                  value={mappingScope}
                  loading={mappingTenantsLoading || mappingLoading}
                  onChange={(scope) => {
                    setMappingScope(scope);
                    // Default the save target to the source just loaded (a tenant saves to itself;
                    // the provider default saves to itself). The user may additionally check the
                    // provider in the "Save to" multi-select to copy a tenant's template up as well.
                    setMappingSaveScopes(scope ? [scope] : []);
                    if (mappingTradeId != null) loadScopeTasks(mappingTradeId, scope);
                  }}
                />
              )}
              {mappingTradeId !== null && mappingData && (
                <MappingSaveTargets
                  // Targets are only ever {the loaded tenant, provider}. Loading from the provider
                  // leaves no tenant option (provider→tenant copy is not a supported direction here).
                  tenant={mappingScope && mappingScope !== PROVIDER_SCOPE
                    ? (mappingTenants.find(t => t.tenantId === mappingScope) ?? null)
                    : null}
                  value={mappingSaveScopes}
                  loading={mappingLoading}
                  onChange={(scopes) => setMappingSaveScopes(scopes)}
                />
              )}
              {mappingTradeId !== null && (
                <NMButton
                  id="naomi-mapping-publish"
                  size="sm"
                  color="primary"
                  icon={<NI.Save/>}
                  disabled={mappingSaving || mappingSaveScopes.length === 0 || !mappingData}
                  onClick={handlePublishAll}>
                  {mappingSaving ? 'Publishing…' : 'Publish question + tasks'}
                </NMButton>
              )}
            </div>
            {mappingSaveScopes.includes(PROVIDER_SCOPE) && (
              <div style={{ margin: '8px 0', padding: '8px 12px', borderRadius: 6, background: '#fdecea', color: '#b71c1c', fontSize: 13, fontWeight: 500 }}>
                Saving to <strong>Provider default</strong> — separate, sensitive action: affects the base every new tenant starts from.
                {mappingScope && mappingScope !== PROVIDER_SCOPE && ' The tenant’s template will also be copied up to the provider default.'}
              </div>
            )}
            {mappingTradeId !== null && !mappingData && !mappingLoading && (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: 14, textAlign: 'center', padding: 24 }}>
                Select a tenant or provider in <strong style={{ margin: '0 4px' }}>Load from</strong> to load its task template.
              </div>
            )}
            {mappingData?.sdm && (
              <MappingPane
                key={mappingData.reloadKey}
                sdm={mappingData.sdm as any}
                onUpdate={(updater) =>
                  setMappingData(d => d ? { ...d, sdm: updater(d.sdm) } : d)
                }
                initialTasks={mappingData.tasks}
                onLinksChanged={(links: LinkDef[], tasks: Task[]) => {
                  const tradeOid: string | undefined =
                    (mappingData.sdm as any)?.GlobalVariables?.tradeOid;
                  if (!tradeOid || tradeOid === 'unknown') return;
                  syncToSession(
                    tradeOid,
                    mappingData.sdm as any,
                    links,
                    tasks,
                    (s: any) => serializeToSdmDocument(s, 'GMS', 'User', tradeOid),
                  );
                }}
              />
            )}
          </div>
        )}
        {view === 'preview' && <PreviewPane sdm={sdm} tradeLogo={isAssetTemplate(sdm) ? undefined : tradeLogo}/>}
        {view === 'catalogue' && <CataloguePane accessToken={accessToken}/>}
        {view === 'versions' && (RichVersionsPaneComp
          ? <RichVersionsPaneComp sdm={sdm} onPublish={() => setPublishDlg(true)}
              onOpenVersion={openVersion}
              onDiscardDraft={() => { setSdm(s => ({ ...s, draft: { ...(s.draft||{}), changedSince: [] }})); }}
              onForkVersion={(h) => {
                const fork = forkSDM(sdm, sdm.GlobalVariables.Name + ' (from v' + h.version + ')');
                setSdm(fork); setView('studio'); setVersionContext(null);
              }}/>
          : <VersionsPane sdm={sdm} onPublish={() => setPublishDlg(true)}
            onOpenVersion={openVersion}
            onDiscardDraft={() => { setSdm(s => ({ ...s, draft: { ...(s.draft||{}), changedSince: [] }})); }}/>)}
        {view === 'library' && <LibraryPane
          trades={[
            ...library,
            ...dbTrades.filter(dt => !library.some(l => l.trade === dt.trade)),
          ]}
          loading={libLoading}
          onNewBlank={() => startBlankTemplate('Untitled trade template')}
          onFork={() => setForkPicker(true)}
          onAsset={openAssetTemplate}
          onDelete={null}
          onOpenTrade={openTrade}/>}
        </div>
      </div>

      {/* Dialogs */}
      <NewQuestionDialog open={newQDlg.open} onClose={() => setNewQDlg({ open: false, groupOid: null })}
        sdm={sdm} defaultGroupOid={newQDlg.groupOid ?? undefined}
        onCreate={(p: any) => { addQuestion(p); setNewQDlg({ open: false, groupOid: null }); }}/>
      <ConditionBuilder id="condition-builder" open={branchDlg.open}
        onClose={() => setBranchDlg({ open: false, target: null, scope: 'question', editing: null })}
        sdm={sdm} editing={branchDlg.editing} scope={branchDlg.scope as any} onSave={saveBranch as any}/>
      <CalcBuilder id="calc-builder" open={calcDlg.open} onClose={() => setCalcDlg({ open: false, itemOid: null })}
        sdm={sdm} targetItem={tv.ItemDef.find(i => i.OID === calcDlg.itemOid)} onSave={saveCalc}/>
      <ConfirmDialog id="confirm-dialog" open={!!confirmDlg} onClose={() => setConfirmDlg(null)}
        onConfirm={confirmDlg?.onConfirm || (()=>{})}
        title={confirmDlg?.title || ''} body={confirmDlg?.body || ''} confirmLabel={confirmDlg?.confirmLabel}/>
      <MappingBuilderDialog open={mappingDlg.open} onClose={() => setMappingDlg({ open: false, editing: null })}
        sdm={sdm} editing={mappingDlg.editing}
        onSave={(newMapping) => {
          const originalName = mappingDlg.editing?.Name;
          setSdm(s => {
            const tv0 = s.TemplateVersion[0];
            const existing = tv0?.Mappings || [];
            const next = originalName
              ? existing.map((m: any) => m.Name === originalName ? newMapping : m)
              : [...existing, newMapping];
              
            const metaKeyByOid: Record<string, string> = {};
            for (const g of (newMapping.Versions?.[0]?.ItemGroupDef || [])) {
              for (const item of (g.ItemDef || [])) {
                if (item.OID && item.Name) metaKeyByOid[item.OID] = item.Name;
              }
            }
            const updatedItemDef = (tv0?.ItemDef || []).map((item: any) =>
              metaKeyByOid[item.OID] !== undefined ? { ...item, MetaKey: metaKeyByOid[item.OID] } : item
            );

            return {
              ...s,
              TemplateVersion: s.TemplateVersion.map((v: any, i: number) =>
                i === 0 ? { ...v, Mappings: next, ItemDef: updatedItemDef } : v
              ),
            };
          });
          setMappingDlg({ open: false, editing: null });
        }}/>
      <input ref={logoFileRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => { handleLogoFile(e.target.files?.[0]); e.target.value = ''; }}/>
      <PublishDialog id="publish-dialog" open={publishDlg} onClose={() => setPublishDlg(false)}
        existingVersions={sdm.TemplateVersion.slice(1).filter((v: any) => v._meta).map(v => v.version)}
        draftVersion={sdm.draft?.version || bumpMinor(tv.version)}
        templateName={sdm.GlobalVariables?.Name}
        onPublish={publish}/>
      {/* Mapping publish result — reuse the app's MDialog with a single OK acknowledgement. */}
      <MDialog
        id="naomi-mapping-publish-result"
        open={!!publishResult}
        onClose={() => setPublishResult(null)}
        title={publishResult?.ok ? 'Published' : 'Publish failed'}
        icon={publishResult?.ok ? <NI.Check/> : <NI.Warn/>}
        width={440}
        actions={
          <NMButton id="naomi-mapping-publish-result-ok" color="primary"
            onClick={() => setPublishResult(null)}>OK</NMButton>
        }>
        <div style={{ fontSize: 14, color: NT.ink2, lineHeight: 1.5 }}>{publishResult?.message}</div>
      </MDialog>
      {publishToast && (
        <div role={publishToast.type === 'error' ? 'alert' : 'status'}
          aria-live={publishToast.type === 'error' ? 'assertive' : 'polite'}
          style={{ position: 'fixed', top: 28, left: '50%', transform: 'translateX(-50%)', zIndex: 1400,
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '13px 16px', borderRadius: 10,
          background: publishToast.type === 'success' ? NT.success : NT.danger,
          color: '#fff', fontSize: 13.5, fontWeight: 500,
          boxShadow: '0 4px 20px rgba(0,0,0,0.20)',
          minWidth: 260, maxWidth: 420,
          animation: 'naomi-fadein .2s ease' }}>
          <span style={{ width: 22, height: 22, borderRadius: 11, border: '2px solid rgba(255,255,255,0.7)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
            {publishToast.type === 'success' ? '✓' : '✕'}
          </span>
          <span style={{ flex: 1, lineHeight: 1.4 }}>{publishToast.message}</span>
          <button onClick={() => setPublishToast(null)}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.75)',
              cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
        </div>
      )}
      {/* No-changes info toast */}
      {saveInfo && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1300, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.35)' }}
          onClick={() => setSaveInfo(null)}>
          <div onClick={e => e.stopPropagation()}
            style={{ width: 420, borderRadius: 12, overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0,0,0,0.22)', background: '#fff' }}>
            <div style={{ background: NT.warnSoft, padding: '28px 24px 20px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 48, height: 48, borderRadius: 24,
                border: `2.5px solid ${NT.warn}`, color: NT.warn,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700 }}>
                i
              </div>
            </div>
            <div style={{ padding: '20px 28px 10px', textAlign: 'center',
              fontSize: 14.5, color: NT.ink2, lineHeight: 1.6 }}>
              {saveInfo}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10,
              padding: '12px 24px 20px' }}>
              <NMButton color="primary" onClick={() => setSaveInfo(null)}>Ok</NMButton>
            </div>
          </div>
        </div>
      )}
      <UnitsManager open={unitsMgr} onClose={() => setUnitsMgr(false)} sdm={sdm} onSave={saveUnits}/>
      {RepeatingGroupConfigComp && (
        <MDialog  id="naomi-studio-dialog-repeating-group" open={repeatDlg.open} onClose={() => setRepeatDlg({ open: false, groupOid: null })}
          title="Configure repeating section" icon={<NI.Repeat/>} width={520}>
          {repeatDlg.groupOid && (() => {
            const gi = findGroupAnywhere(tv, repeatDlg.groupOid);
            if (!gi) return null;
            let summarizeInstances = gi.group?.SummarizeInstances === 'Yes';
            if (!summarizeInstances) {
              for (const f of (tv.FormDef || [])) {
                const r = (f.ItemGroupRef || []).find(x => x.ItemGroupOID === repeatDlg.groupOid);
                if ((r as any)?.SummarizeInstances === 'Yes') { summarizeInstances = true; break; }
              }
            }
            return (
              <RepeatingGroupConfigComp group={gi.group} sdm={sdm}
                summarizeInstances={summarizeInstances}
                onChange={(patch) => {
                  const { __clearMethodOID, __summarizeInstances, ...cleanPatch } = patch;
                  setSdm(s => ({ ...s, TemplateVersion: s.TemplateVersion.map((v, i) => {
                    if (i !== 0) return v;
                    let next = { ...v, ItemGroupDef: mapGroup(v, repeatDlg.groupOid, g => ({ ...g, ...cleanPatch })) };
                    if (__summarizeInstances !== undefined) {
                      next = {
                        ...next,
                        ItemGroupDef: mapGroup(next, repeatDlg.groupOid, g => {
                          if (__summarizeInstances) return { ...g, SummarizeInstances: 'Yes' };
                          const { SummarizeInstances: _si, ...rest } = g as any;
                          return rest;
                        }),
                        FormDef: (next.FormDef || []).map(f => ({
                          ...f,
                          ItemGroupRef: (f.ItemGroupRef || []).map(r => {
                            if (r.ItemGroupOID !== repeatDlg.groupOid) return r;
                            if (__summarizeInstances) return { ...r, SummarizeInstances: 'Yes' };
                            const { SummarizeInstances: _si, ...rest } = r as any;
                            return rest;
                          }),
                        })),
                      };
                    }
                    const repeatFromOID = (cleanPatch as any).RepeatFromOID as string | null | undefined;
                    const groupInfo = findGroupAnywhere(v, repeatDlg.groupOid);
                    const isSubgroup = !!groupInfo?.parentOid;
                    if (repeatFromOID) {
                      let existingMethodOid: string | undefined;
                      if (isSubgroup) {
                        existingMethodOid = (groupInfo?.group as any)?.MethodOID;
                      } else {
                        for (const f of (v.FormDef || [])) {
                          const r = (f.ItemGroupRef || []).find(x => x.ItemGroupOID === repeatDlg.groupOid);
                          if (r?.MethodOID) { existingMethodOid = r.MethodOID; break; }
                        }
                      }
                      const meths = next.MethodDef;
                      const nextN = (meths.length ? Math.max(...meths.map(m => parseInt(m.OID.replace(/\D/g, '')) || 0)) : 0) + 1;
                      const rcMethodOid = existingMethodOid ?? `M.${nextN}`;
                      const rcMethod = {
                        OID: rcMethodOid,
                        FormalExpression: { Context: 'OpenEDC', text: `!(${repeatFromOID} : RepeatCount)` },
                        humanReadable: undefined as any,
                        kind: 'repeat' as const,
                      };
                      const mdIdx = meths.findIndex(m => m.OID === rcMethodOid);
                      next = {
                        ...next,
                        MethodDef: mdIdx >= 0
                          ? meths.map(m => m.OID === rcMethodOid ? rcMethod : m)
                          : [...meths, rcMethod],
                      };
                      if (isSubgroup) {
                        next = { ...next, ItemGroupDef: mapGroup(next, repeatDlg.groupOid, g => ({ ...g, MethodOID: rcMethodOid })) };
                      } else {
                        next = {
                          ...next,
                          FormDef: (next.FormDef || []).map(f => ({
                            ...f,
                            ItemGroupRef: (f.ItemGroupRef || []).map(r =>
                              r.ItemGroupOID === repeatDlg.groupOid ? { ...r, MethodOID: rcMethodOid } : r
                            ),
                          })),
                        };
                      }
                    } else if (__clearMethodOID) {
                      let orphanedOid: string | undefined;
                      if (isSubgroup) {
                        orphanedOid = (groupInfo?.group as any)?.MethodOID;
                      } else {
                        for (const f of (v.FormDef || [])) {
                          const r = (f.ItemGroupRef || []).find(x => x.ItemGroupOID === repeatDlg.groupOid);
                          if (r?.MethodOID) { orphanedOid = r.MethodOID; break; }
                        }
                      }
                      next = {
                        ...next,
                        MethodDef: orphanedOid ? next.MethodDef.filter(m => m.OID !== orphanedOid) : next.MethodDef,
                      };
                      if (isSubgroup) {
                        next = { ...next, ItemGroupDef: mapGroup(next, repeatDlg.groupOid, g => { const { MethodOID: _m, ...rest } = g as any; return rest; }) };
                      } else {
                        next = {
                          ...next,
                          FormDef: (next.FormDef || []).map(f => ({
                            ...f,
                            ItemGroupRef: (f.ItemGroupRef || []).map(r => {
                              if (r.ItemGroupOID === repeatDlg.groupOid && r.MethodOID) {
                                const { MethodOID: _m, ...rest } = r as any;
                                return rest;
                              }
                              return r;
                            }),
                          })),
                        };
                      }
                    }
                    const finalGi = findGroupAnywhere(next, repeatDlg.groupOid);
                    const finalSi = (finalGi?.group as any)?.SummarizeInstances;
                    next = {
                      ...next,
                      FormDef: (next.FormDef || []).map(f => ({
                        ...f,
                        ItemGroupRef: (f.ItemGroupRef || []).map(r => {
                          if (r.ItemGroupOID !== repeatDlg.groupOid) return r;
                          if (finalSi === 'Yes') return { ...r, SummarizeInstances: 'Yes' };
                          const { SummarizeInstances: _si, ...rest } = r as any;
                          return rest;
                        }),
                      })),
                    };
                    return next;
                  }) }));
                }}/>
            );
          })()}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18, paddingTop: 14, borderTop: `1px solid ${NT.line}` }}>
            <NMButton id="naomi-studio-button-repeat-dlg-done" variant="text" color="neutral" onClick={() => setRepeatDlg({ open: false, groupOid: null })}>Done</NMButton>
          </div>
        </MDialog>
      )}
      {CustomTypeBuilderComp && (
        <CustomTypeBuilderComp open={customTypeDlg.open} onClose={() => setCustomTypeDlg({ open: false, editing: null, attachToOid: null })}
          sdm={sdm} editing={customTypeDlg.editing}
          onSave={(def) => {
            const id = customTypeDlg.editing?.id || `CT.${Date.now().toString(36)}`;
            const next = { ...getNaomiRegistry().customTypes, [id]: { ...def, id } };
            setNaomiCustomTypes(next);
            setCustomTypes(next);
            if (customTypeDlg.attachToOid) {
              updateItem(customTypeDlg.attachToOid, { CompositeRef: id });
            }
          }}/>
      )}
      <ForkPickerDialog id="fork-picker-dialog" open={forkPicker} onClose={() => setForkPicker(false)}
        trades={[...library, ...dbTrades.filter(dt => !library.some((l: any) => l.trade === dt.trade))]}
        onPick={(t) => { forkFromTrade(t); }}/>
      <MDialog id="naomi-studio-dialog-rename-section" open={!!renaming} onClose={() => setRenaming(null)} title="Rename section" icon={<NI.Edit/>} width={420}
        actions={<>
          <NMButton id="naomi-studio-button-rename-cancel" variant="text" color="neutral" onClick={() => setRenaming(null)}>Cancel</NMButton>
          <NMButton id="naomi-studio-button-rename-save" color="primary" onClick={() => { renameGroup(renaming!.groupOid, renaming!.current.trim() || 'Untitled'); setRenaming(null); }}>Save</NMButton>
        </>}>
        <MTextField id="naomi-studio-input-rename-section" label="Section name" autoFocus value={renaming?.current || ''}
          onChange={v => setRenaming(r => r ? { ...r, current: v } : r)}/>
      </MDialog>

      {GroupHeaderImageDialogComp && (() => {
        const gi = bannerDlg.groupOid ? findGroupAnywhere(tv, bannerDlg.groupOid) : null;
        return (
          <GroupHeaderImageDialogComp
            open={bannerDlg.open}
            onClose={() => setBannerDlg({ open: false, groupOid: null })}
            group={gi?.group || null}
            onSave={(url) => {
              if (bannerDlg.groupOid) updateGroup(bannerDlg.groupOid, { HeaderImage: url || undefined });
              setBannerDlg({ open: false, groupOid: null });
            }}/>
        );
      })()}
    </div>
  );
}

function PreviewPane({ sdm, tradeLogo }: { sdm: any; tradeLogo?: string }) {
  const { accessToken: previewToken } = useOidcAccessToken();
  const [catalogueSasMap, setCatalogueSasMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!previewToken) return;
    getCatalogueImages(previewToken).then(images => {
      const map: Record<string, string> = {};
      for (const img of images) {
        const base = img.baseUrl ?? '';
        if (img.imageUrl && img.previewUrl) {
          map[img.imageUrl] = img.previewUrl;
          map[base + img.imageUrl] = img.previewUrl;
        }
        if (img.thumbnailUrl && img.thumbnailPreviewUrl) {
          map[img.thumbnailUrl] = img.thumbnailPreviewUrl;
          map[base + img.thumbnailUrl] = img.thumbnailPreviewUrl;
        }
      }
      setCatalogueSasMap(map);
    }).catch(() => {});
  }, [previewToken]);

  return (
    <div style={{ flex: 1, display: 'flex', minWidth: 0 }}>
      <div style={{ flex: 1, padding: '20px 0', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ width: '100%', maxWidth: 540, flex: 1, minHeight: 0, background: NT.surface,
          border: `1px solid ${NT.line}`, borderRadius: 12, marginInline: 24, boxShadow: NT.shadow2, overflow: 'hidden',
          display: 'flex', flexDirection: 'column' }}>
          {tradeLogo && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '18px 18px 0', flexShrink: 0 }}>
              <div style={{ width: 220, background: NT.surface, border: `1px solid ${NT.line}`,
                borderRadius: 12, boxShadow: NT.shadow2, overflow: 'hidden' }}>
                <img src={tradeLogo} alt={`${sdm.GlobalVariables.Name} logo`}
                  style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }}/>
                <div style={{ padding: '10px 12px', textAlign: 'center', fontSize: 14.5, fontWeight: 700, color: NT.ink }}>
                  {sdm.GlobalVariables.Name}
                </div>
              </div>
            </div>
          )}
          <div style={{ flex: 1, minHeight: 0 }}>
            <LivePreview sdm={sdm} fillSample={false} catalogueSasMap={catalogueSasMap}/>
          </div>
        </div>
      </div>
    </div>
  );
}

function VersionsPane({ sdm, onPublish, onOpenVersion, onDiscardDraft }) {
  const history = sdm.history || [];
  const draft = sdm.draft;
  const tv = sdm.TemplateVersion[0];
  const hasPublished = history.length > 0;
  const publishedVersions = sdm.TemplateVersion.slice(1);
  const cumulativeCounts: Record<string, { q: number; s: number }> = {};
  let acc: any = null;
  for (const pv of publishedVersions) {
    acc = acc ? mergeVersions(acc, pv) : { ...pv };
    cumulativeCounts[pv.version] = {
      q: (acc.ItemDef?.length ?? 0),
      s: (acc.ItemGroupDef?.length ?? 0),
    };
  }

  return (
    <div style={{ flex: 1, padding: 24, overflow: 'auto' }} className="naomi-scroll">
      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: NT.ink, marginBottom: 4 }}>Version history</div>
        <div style={{ fontSize: 13, color: NT.ink3, marginBottom: 24 }}>All published versions of the {sdm.GlobalVariables.Name} template.</div>

        {/* Draft card — shown only before first publish */}
        {draft && !hasPublished && (
          <div style={{ background: NT.surface, border: `2px solid ${NT.primary}`, borderRadius: 10, padding: 18, marginBottom: 24,
            boxShadow: NT.shadow2, position: 'relative' }}>
            <div style={{ position: 'absolute', top: -10, left: 16, background: NT.primary, color: '#fff',
              padding: '2px 10px', borderRadius: 4, fontSize: 10.5, fontWeight: 700, letterSpacing: .5 }}>WORKING DRAFT</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: NT.ink, fontFamily: NT.fontMono }}>v{draft.version}</div>
              <div style={{ fontSize: 12.5, color: NT.ink3 }}>{tv.ItemDef.length} questions · {tv.ItemGroupDef.length} sections</div>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 14 }}>
              <NMButton id="naomi-studio-button-versions-publish-draft" size="sm" color="primary" icon={<NI.Rocket/>} onClick={onPublish}>Publish v{draft.version}</NMButton>
              <NMButton id="naomi-studio-button-versions-open-draft" size="sm" variant="outlined" color="neutral" icon={<NI.Edit/>}
                onClick={() => onOpenVersion({ version: draft.version, status: 'draft', author: 'You', date: 'now', note: 'Working draft', changes: (draft.changedSince||[]).length })}>Open in builder</NMButton>
            </div>
          </div>
        )}

        {/* Draft card shown after first publish */}
        {draft && hasPublished && (
          <div style={{ background: NT.surface, border: `2px solid ${NT.primary}`, borderRadius: 10, padding: 18, marginBottom: 24,
            boxShadow: NT.shadow2, position: 'relative' }}>
            <div style={{ position: 'absolute', top: -10, left: 16, background: NT.primary, color: '#fff',
              padding: '2px 10px', borderRadius: 4, fontSize: 10.5, fontWeight: 700, letterSpacing: .5 }}>WORKING DRAFT</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: NT.ink, fontFamily: NT.fontMono }}>v{draft.version}</div>
              </div>
              <div style={{ fontSize: 12, color: NT.ink4 }}>
                {tv.ItemDef.length} question{tv.ItemDef.length !== 1 ? 's' : ''} · {tv.ItemGroupDef.length} section{tv.ItemGroupDef.length !== 1 ? 's' : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 14 }}>
              <NMButton id="naomi-studio-button-versions-publish-draft" size="sm" color="primary" icon={<NI.Rocket/>} onClick={onPublish}>Publish v{draft.version}</NMButton>
              <NMButton id="naomi-studio-button-versions-open-draft" size="sm" variant="outlined" color="neutral" icon={<NI.Edit/>}
                onClick={() => onOpenVersion({ version: draft.version, status: 'draft', author: 'You', date: 'now', note: 'Working draft', changes: (draft.changedSince||[]).length })}>Open in builder</NMButton>
            </div>
          </div>
        )}

        <div style={{ position: 'relative', paddingLeft: 24 }}>
          <div style={{ position: 'absolute', left: 7, top: 6, bottom: 6, width: 2, background: NT.line }}/>
          {history.map((h, i) => {
            const counts = cumulativeCounts[h.version];
            const qCount = counts?.q ?? h.questionCount ?? 0;
            const sCount = counts?.s ?? h.sectionCount ?? 0;
            return (
              <VersionRow key={h.version + i} entry={h}
                questionCount={qCount} sectionCount={sCount}/>
            );
          })}
          {!history.length && (
            <div style={{ fontSize: 12.5, color: NT.ink4, padding: 14, fontStyle: 'italic' }}>
              No published versions yet — publish your first version from the editor.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function VersionRow({ entry: h, questionCount, sectionCount }) {
  return (
    <div style={{ position: 'relative', marginBottom: 18 }}>
      <div style={{ position: 'absolute', left: -22, top: 12, width: 14, height: 14, borderRadius: 7,
        background: h.status==='published' ? NT.success : NT.line2, border: `3px solid ${NT.bg}` }}/>
      <div style={{ background: NT.surface,
          border: `1px solid ${NT.line}`, borderRadius: 8, padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: NT.ink, fontFamily: NT.fontMono }}>v{h.version}</div>
            <NMChip color={h.status==='published'?'success':'neutral'}>{h.status==='published'?'LIVE':h.status}</NMChip>
            {(h.date || h.author) && (
              <div style={{ fontSize: 12, color: NT.ink3 }}>
                {h.date && <span>{h.date}</span>}
                {h.date && h.author && <span> · </span>}
                {h.author && <span>by {h.author}</span>}
              </div>
            )}
          </div>
          <div style={{ fontSize: 12, color: NT.ink4, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {questionCount} question{questionCount !== 1 ? 's' : ''} · {sectionCount} section{sectionCount !== 1 ? 's' : ''}
          </div>
        </div>
        <div style={{ fontSize: 13, color: NT.ink2, marginTop: 6 }}>{h.note}</div>
      </div>
    </div>
  );
}

function LibraryPane({ onNewBlank, onFork, onAsset, onOpenTrade, onDelete, trades, loading }) {
  const allTrades = trades || [];
  const [filter, setFilter] = useState('');
  const filtered = allTrades.filter(t => !filter ||
    t.trade.toLowerCase().includes(filter.toLowerCase()) ||
    t.category.toLowerCase().includes(filter.toLowerCase()));
  return (
    <div style={{ flex: 1, padding: 24, overflow: 'auto' }} className="naomi-scroll">
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: NT.ink }}>Trade template library</div>
            <div style={{ fontSize: 13, color: NT.ink3, marginTop: 2 }}>
              {loading ? 'Loading…' : `${allTrades.length} trade${allTrades.length !== 1 ? 's' : ''}`}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ width: 220 }}>
              <MTextField id="naomi-studio-input-library-search" placeholder="Search trades…" value={filter} onChange={setFilter} size="sm" adornment={<NI.Search/>}/>
            </div>
            <NMButton id="naomi-studio-button-library-fork-existing" variant="outlined" color="neutral" icon={<NI.Fork/>} onClick={onFork}>Fork existing</NMButton>
            <NMButton id="naomi-studio-button-library-new-blank" color="primary" icon={<NI.Plus/>} onClick={onNewBlank}>New blank template</NMButton>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
          <button id="naomi-studio-card-library-new-blank" onClick={onNewBlank} style={{ textAlign: 'left', cursor: 'pointer',
            background: NT.surface, border: `1px solid ${NT.line}`, borderRadius: 10, padding: 16,
            display: 'flex', gap: 12, alignItems: 'flex-start' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = NT.primary; e.currentTarget.style.boxShadow = NT.shadow2; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = NT.line; e.currentTarget.style.boxShadow = 'none'; }}>
            <div style={{ width: 40, height: 40, borderRadius: 8, background: NT.primarySoft, color: NT.primary,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <NI.Plus/>
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: NT.ink, marginBottom: 2 }}>Start from scratch</div>
              <div style={{ fontSize: 12.5, color: NT.ink3, lineHeight: 1.45 }}>Create a brand-new trade template with no questions. Best when the trade is unique and unlike anything else.</div>
            </div>
          </button>
          <button id="naomi-studio-card-library-fork-existing" onClick={onFork} style={{ textAlign: 'left', cursor: 'pointer',
            background: NT.surface, border: `1px solid ${NT.line}`, borderRadius: 10, padding: 16,
            display: 'flex', gap: 12, alignItems: 'flex-start' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = NT.logic; e.currentTarget.style.boxShadow = NT.shadow2; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = NT.line; e.currentTarget.style.boxShadow = 'none'; }}>
            <div style={{ width: 40, height: 40, borderRadius: 8, background: NT.logicSoft, color: NT.logic,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <NI.Fork/>
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: NT.ink, marginBottom: 2 }}>Fork from existing</div>
              <div style={{ fontSize: 12.5, color: NT.ink3, lineHeight: 1.45 }}>Clone an existing template and edit. Inherits questions, branching and calculations as a starting point.</div>
            </div>
          </button>
          <button id="naomi-studio-card-library-asset-questions" onClick={onAsset} style={{ textAlign: 'left', cursor: 'pointer',
            background: NT.surface, border: `1px solid ${NT.line}`, borderRadius: 10, padding: 16,
            display: 'flex', gap: 12, alignItems: 'flex-start' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = NT.warn; e.currentTarget.style.boxShadow = NT.shadow2; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = NT.line; e.currentTarget.style.boxShadow = 'none'; }}>
            <div style={{ width: 40, height: 40, borderRadius: 8, background: NT.warnSoft, color: NT.warn,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <NI.Book/>
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: NT.ink, marginBottom: 2 }}>Asset Questions</div>
              <div style={{ fontSize: 12.5, color: NT.ink3, lineHeight: 1.45 }}>Edit the application-wide asset question template. One template applies to all trades.</div>
            </div>
          </button>
        </div>

        <div style={{ fontSize: 11, fontWeight: 700, color: NT.ink3, textTransform: 'uppercase', letterSpacing: .5, marginBottom: 10 }}>
          All trade templates
        </div>
        {loading && allTrades.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: NT.ink4, fontSize: 13 }}>
            Loading…
          </div>
        ) : !loading && allTrades.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: NT.ink4, fontSize: 13 }}>
            No trade templates yet. Create one using the buttons above.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
            {filtered.map(t => (
              <LibraryCard key={t.trade + (t.tradeOid || t.updated)} trade={t} onOpen={() => onOpenTrade(t)}
                onDelete={onDelete ? () => onDelete(t) : null}/>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LibraryCard({ trade: t, onOpen, onDelete }) {
  const [hover, setHover] = useState(false);
  return (
    <div id={`naomi-library-card-trade-${t.trade.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
      style={{ background: NT.surface, border: `1px solid ${hover ? NT.primary : NT.line}`, borderRadius: 10,
      padding: 18, cursor: 'pointer', transition: 'all .12s', position: 'relative',
      boxShadow: hover ? NT.shadow2 : 'none', transform: hover ? 'translateY(-1px)' : 'none' }}
      onClick={onOpen}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: 8, background: t.color, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>
          {t.trade.slice(0,1)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: NT.ink }}>{t.trade}</div>
          <div style={{ fontSize: 11, color: NT.ink4 }}>{t.category}</div>
        </div>
        <NMChip color={t.status==='published'?'success':t.status==='draft'?'warn':'neutral'}>{t.status}</NMChip>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: NT.ink3 }}>
        <span>{t.items} questions</span>
        <span style={{ color: NT.line2 }}>·</span>
        <span style={{ fontFamily: NT.fontMono }}>v{t.version}</span>
        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: NT.ink4 }}>{t.updated}</span>
      </div>
      {hover && (
        <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', gap: 4 }}>
          {onDelete && (
            <button id={`naomi-studio-button-library-card-delete-${t.trade}`} onClick={e => { e.stopPropagation(); onDelete(); }}
              className="nm-icon-btn" title="Delete this template"
              style={{ background: NT.surface, color: NT.danger, width: 26, height: 26, borderRadius: 4,
                border: `1px solid ${NT.line2}` }}
              onMouseEnter={e => { e.currentTarget.style.background = NT.danger; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = NT.surface; e.currentTarget.style.color = NT.danger; }}>
              <NI.Trash/>
            </button>
          )}
          <button id={`naomi-studio-button-library-card-open-${t.trade}`} onClick={e => { e.stopPropagation(); onOpen(); }}
            className="nm-icon-btn" title="Open template"
            style={{ background: NT.primary, color: '#fff', width: 26, height: 26, borderRadius: 4 }}>
            <NI.Chev d="right"/>
          </button>
        </div>
      )}
    </div>
  );
}

Object.assign(window, { NaomiStudio });
export { NaomiStudio };
